import { clearQueryCache, peekQuery, putQuery, readQuery } from './queryCache';
import { ECOMMERCE_API_BASE_URL } from '../config';
import { apiErrorMessage, NETWORK_ERROR_MESSAGE, readErrorBody } from './errorMessage';

const BASE = `${ECOMMERCE_API_BASE_URL}/erp`;
const PRODUCT_BASE = `${ECOMMERCE_API_BASE_URL}/product`;

export class ApiRequestError extends Error {
    status: number;
    body: unknown;
    data: unknown;

    constructor(message: string, status: number, body: unknown) {
        super(message);
        this.name = 'ApiRequestError';
        this.status = status;
        this.body = body;
        this.data = body && typeof body === 'object' && 'data' in body
            ? (body as { data?: unknown }).data
            : body;
        Object.setPrototypeOf(this, ApiRequestError.prototype);
    }
}

const responseErrorBody = readErrorBody;

function responseErrorMessage(res: Response, body: unknown): string {
    const data = body as any;
    const detail = data && typeof data === 'object' && data.error && typeof data.error === 'object' ? data.error : null;
    if (!detail || Array.isArray(detail)) return apiErrorMessage(res.status, body);
    const message = detail.code === 'missing_product_measurements'
        ? '请先补全商品尺寸和重量'
        : apiErrorMessage(res.status, { error: detail.message, request_id: data.request_id });
    const missingFields = Array.isArray(detail.missing_fields)
        ? detail.missing_fields
            .map((field: unknown) => RECEIVE_FIELD_LABELS[String(field)])
            .filter(Boolean)
        : [];
    return missingFields.length ? `${message}（缺少：${missingFields.join('、')}）` : message;
}

async function apiError(res: Response): Promise<ApiRequestError> {
    const body = await responseErrorBody(res);
    return new ApiRequestError(responseErrorMessage(res, body), res.status, body);
}

const RECEIVE_FIELD_LABELS: Record<string, string> = {
    height: '高度',
    width: '宽度',
    depth: '长度',
    weight: '重量',
};

async function erpFetch(url: string, options?: RequestInit): Promise<Response> {
    let response: Response;
    try {
        response = await fetch(url, options);
    } catch (error) {
        // fetch() rejects with an English TypeError when the server is unreachable.
        if (error instanceof Error && error.name === 'AbortError') throw error;
        throw new ApiRequestError(NETWORK_ERROR_MESSAGE, 0, null);
    }
    if (response.ok && !['GET', 'HEAD'].includes((options?.method ?? 'GET').toUpperCase())) clearQueryCache();
    return response;
}

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
    if (!['GET', 'HEAD'].includes((opts?.method ?? 'GET').toUpperCase()) && /^\/livestreams\/\d+(?:\/|$|\?)/.test(path)) {
        path += `${path.includes('?') ? '&' : '?'}view=workspace`;
    }
    const load = async (signal?: AbortSignal) => {
        const res = await erpFetch(`${BASE}${path}`, {
            ...opts, signal: signal ?? opts?.signal,
            headers: { 'Content-Type': 'application/json', ...opts?.headers },
        });
        if (!res.ok) {
            const body = await responseErrorBody(res);
            throw new ApiRequestError(responseErrorMessage(res, body), res.status, body);
        }
        return res.json() as Promise<T>;
    };
    if ((opts?.method ?? 'GET').toUpperCase() !== 'GET') return load();
    return readQuery(`raw|${BASE}${path}`, load, {
        signal: opts?.signal ?? undefined,
        staleTime: /^\/(livestreams|procurements|inventory|streamers|suppliers|product-categories|warehouses|livestream-types|product-attributes)(?:\?|$)/.test(path) ? 30_000 : 0,
    });
}

async function productJsonRequest<T>(path: string, opts?: RequestInit): Promise<T> {
    const res = await erpFetch(`${PRODUCT_BASE}${path}`, {
        headers: { 'Content-Type': 'application/json', ...opts?.headers },
        ...opts,
    });
    if (!res.ok) {
        const body = await responseErrorBody(res);
        throw new ApiRequestError(responseErrorMessage(res, body), res.status, body);
    }
    return res.json();
}

async function productFormRequest<T>(path: string, form: FormData): Promise<T> {
    const res = await erpFetch(`${PRODUCT_BASE}${path}`, {
        method: 'POST',
        body: form,
    });
    if (!res.ok) {
        const body = await responseErrorBody(res);
        throw new ApiRequestError(responseErrorMessage(res, body), res.status, body);
    }
    return res.json();
}

async function erpFormRequest<T>(path: string, form: FormData): Promise<T> {
    const res = await erpFetch(`${BASE}${path}`, {
        method: 'POST',
        body: form,
    });
    if (!res.ok) {
        const body = await responseErrorBody(res);
        throw new ApiRequestError(responseErrorMessage(res, body), res.status, body);
    }
    return res.json();
}

// ─── Streamers ────────────────────────────────────────────────────────────────

export interface ApiStreamer {
    id: number;
    name: string;
    note: string;
    /** Delivery phone. A warehouse names one of these people as its 收件人. */
    phone: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export const getStreamers = (includeInactive = false) =>
    request<ApiStreamer[]>(`/streamers${includeInactive ? '?include_inactive=true' : ''}`);
export const createStreamer = (payload: { name: string; note?: string; phone?: string }) =>
    request<ApiStreamer>('/streamers', { method: 'POST', body: JSON.stringify(payload) });
export const updateStreamer = (id: number, payload: Partial<{ name: string; note: string; phone: string; is_active: boolean }>) =>
    request<ApiStreamer>(`/streamers/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteStreamer = (id: number) =>
    request<{ message: string }>(`/streamers/${id}`, { method: 'DELETE' });

// ─── Suppliers ────────────────────────────────────────────────────────────────

export interface ApiSupplier {
    id: number;
    name: string;
    /** 联系方式。改它不影响任何人登录。 */
    contact_phone: string | null;
    /** 登录账号（登录 = 手机号 + 密码），由团队在货盘里设置。这里只读。 */
    login_phone: string | null;
    wechat: string | null;
    notes: string | null;
    brand_story: string | null;
    /** 产地：供应商在 App 的「我的 → 地点」里填的。ERP 读这一份。 */
    location: { country: string | null; city: string | null; display_name: string | null } | null;
    /** 旧的产地字典。前端已不再维护，保留字段供过渡期人工清理和迁移。 */
    origin_id: number | null;
    origin: ApiSupplierOrigin | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface ApiSupplierOrigin {
    id: number;
    country_code: string;
    country_name: string;
    city_name: string;
    display_name: string;
    sort_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export const getSupplierOrigins = (includeInactive = false) => request<ApiSupplierOrigin[]>(`/supplier-origins${includeInactive ? '?include_inactive=true' : ''}`);
export const createSupplierOrigin = (payload: { country_code: string; country_name: string; city_name: string; display_name?: string; sort_order?: number }) =>
    request<ApiSupplierOrigin>('/supplier-origins', { method: 'POST', body: JSON.stringify(payload) });
export const updateSupplierOrigin = (id: number, payload: Partial<{ country_code: string; country_name: string; city_name: string; display_name: string; sort_order: number; is_active: boolean }>) =>
    request<ApiSupplierOrigin>(`/supplier-origins/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteSupplierOrigin = (id: number) => request<ApiSupplierOrigin>(`/supplier-origins/${id}`, { method: 'DELETE' });

export const getSuppliers = () => request<ApiSupplier[]>('/procurement-suppliers');
export const createSupplier = (payload: { name: string; contact_phone?: string; wechat?: string; notes?: string; brand_story?: string }) =>
    request<ApiSupplier>('/procurement-suppliers', { method: 'POST', body: JSON.stringify(payload) });
export const updateSupplier = (id: number, payload: { name?: string; contact_phone?: string; wechat?: string; notes?: string; brand_story?: string }) =>
    request<ApiSupplier>(`/procurement-suppliers/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteSupplier = (id: number) =>
    request<ApiSupplier>(`/procurement-suppliers/${id}`, { method: 'DELETE' });

export interface ApiPricingRuleConfig {
    currency: string;
    size_tiers_cm: Array<{ max: number; price: number }>;
    category_size_tiers_cm?: Record<string, Array<{ max: number; price: number }>>;
    oversize_price_per_cm: number;
    weight_free_kg: number;
    weight_step_kg: number;
    weight_step_price: number;
    platform_fee_rate: number;
    domestic_freight_rate: number;
    low_budget_cny: number;
    target_profit_usd?: number;
    category_target_profit_usd?: Record<string, number>;
    attribute_premiums: Array<{ attribute_id?: number; attribute_key: string; option_id?: number; option_name: string; rate: number }>;
    supplier_premiums: Array<{ supplier_id: number; supplier_name?: string; rate: number }>;
    cost: Record<string, number>;
}

export interface ApiPricingRuleVersion {
    id: number;
    version: string;
    status: 'draft' | 'published' | 'superseded';
    config: ApiPricingRuleConfig;
    note: string | null;
    published_at: string | null;
    created_at: string;
    updated_at: string;
}

export const getPricingRules = () => request<{
    active: ApiPricingRuleVersion | null;
    versions: ApiPricingRuleVersion[];
    defaults: ApiPricingRuleConfig;
}>('/pricing-rules');
export const createPricingRuleDraft = (payload: { config: ApiPricingRuleConfig; note?: string }) =>
    request<ApiPricingRuleVersion>('/pricing-rules', { method: 'POST', body: JSON.stringify(payload) });
export const updatePricingRuleDraft = (id: number, payload: { config: ApiPricingRuleConfig; note?: string }) =>
    request<ApiPricingRuleVersion>(`/pricing-rules/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const publishPricingRule = (id: number) =>
    request<ApiPricingRuleVersion>(`/pricing-rules/${id}/publish`, { method: 'POST' });

// ─── Product Categories ───────────────────────────────────────────────────────

export interface ApiProductCategory {
    id: number;
    name: string;
    white_bg_prompt: string | null;
    lifestyle_prompt: string | null;
    notes: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export const getProductCategories = () => request<ApiProductCategory[]>('/product-categories');
export const createProductCategory = (payload: { name: string; white_bg_prompt: string; lifestyle_prompt: string; notes?: string }) =>
    request<ApiProductCategory>('/product-categories', { method: 'POST', body: JSON.stringify(payload) });
export const updateProductCategory = (id: number, payload: { name?: string; white_bg_prompt?: string; lifestyle_prompt?: string; notes?: string }) =>
    request<ApiProductCategory>(`/product-categories/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteProductCategory = (id: number) =>
    request<ApiProductCategory>(`/product-categories/${id}`, { method: 'DELETE' });

// ─── Warehouses ───────────────────────────────────────────────────────────────

export interface ApiWarehouse {
    id: number;
    code: string;
    name: string;
    note: string;
    is_active: boolean;
    sort_order: number;
    warehouse_type: 'physical' | 'non_stock';
    inventory_location_id: number | null;
    inventory_location_code: string | null;
    inventory_location_name: string | null;
    /** Where suppliers ship to. Copied onto an order when it is pushed. */
    address: string | null;
    /** 收件人, chosen from the 直播团队; the phone travels with them. */
    recipient_streamer_id: number | null;
    recipient_name: string | null;
    recipient_phone: string | null;
    created_at: string;
    updated_at: string;
}

export const getWarehouses = (includeInactive = false) =>
    request<ApiWarehouse[]>(`/procurement-warehouses${includeInactive ? '?include_inactive=true' : ''}`);
export const createWarehouse = (payload: { name: string; note?: string; code?: string; inventory_location_id: number; warehouse_type?: 'physical' | 'non_stock'; address?: string; recipient_streamer_id?: number | null }) =>
    request<ApiWarehouse>('/procurement-warehouses', { method: 'POST', body: JSON.stringify(payload) });
export const updateWarehouse = (id: number, payload: Partial<{ name: string; note: string; is_active: boolean; inventory_location_id: number; warehouse_type: 'physical' | 'non_stock'; address: string; recipient_streamer_id: number | null }>) =>
    request<ApiWarehouse>(`/procurement-warehouses/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteWarehouse = (id: number) =>
    request<{ message: string }>(`/procurement-warehouses/${id}`, { method: 'DELETE' });

// ─── Live Types (read-only) ───────────────────────────────────────────────────

export interface ApiLiveType {
    id: string;
    value: string;
    name: string;
    label: string;
    location_code: string;
    is_active: boolean;
}

export const getLiveTypes = () => request<ApiLiveType[]>('/livestream-types');

// ─── Product Attributes ───────────────────────────────────────────────────────

export interface ApiAttributeOption {
    id: number;
    attribute_id: number;
    name: string;
    is_active: boolean;
    sort_order: number;
}

export interface ApiProductAttribute {
    id: number;
    key: string;
    name: string;
    note: string;
    is_required: boolean;
    selection_mode: 'single' | 'multiple';
    default_option_id: number | null;
    visibility_rule: { attribute_id: number; option_ids: number[] } | null;
    is_active: boolean;
    sort_order: number;
    option_count: number;
    options: ApiAttributeOption[];
    created_at: string;
    updated_at: string;
}

export const getProductAttributes = (includeInactive = false) =>
    request<ApiProductAttribute[]>(`/product-attributes?include_options=true${includeInactive ? '&include_inactive=true' : ''}`);

/** The six active settings lists in one read; each equals its own endpoint's default read. */
export interface ApiSettingsSnapshotData {
    streamers: ApiStreamer[];
    suppliers: ApiSupplier[];
    product_categories: ApiProductCategory[];
    warehouses: ApiWarehouse[];
    livestream_types: ApiLiveType[];
    product_attributes: ApiProductAttribute[];
}
export type ApiSettingsSnapshot =
    | { version: string; unchanged: true }
    | { version: string; unchanged: false; data: ApiSettingsSnapshotData };
export const getSettingsSnapshot = (known?: string) =>
    request<ApiSettingsSnapshot>(`/settings-snapshot${known ? `?known=${encodeURIComponent(known)}` : ''}`);
export const createProductAttribute = (payload: { name: string; note?: string; key?: string; is_required?: boolean; selection_mode?: 'single' | 'multiple' }) =>
    request<ApiProductAttribute>('/product-attributes', { method: 'POST', body: JSON.stringify(payload) });
export const updateProductAttribute = (id: number, payload: Partial<{ name: string; note: string; is_active: boolean; is_required: boolean; selection_mode: 'single' | 'multiple'; default_option_id: number | null; visibility_rule: { attribute_id: number; option_ids: number[] } | null }>) =>
    request<ApiProductAttribute>(`/product-attributes/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteProductAttribute = (id: number) =>
    request<{ message: string }>(`/product-attributes/${id}`, { method: 'DELETE' });

export interface ApiProductLabelDefinition {
    id: number;
    value: string;
    display_name: string;
    group_name: string;
    sort_order: number;
    is_active: boolean;
}

export const getProductLabels = (includeInactive = false) =>
    request<ApiProductLabelDefinition[]>(`/product-labels${includeInactive ? '?include_inactive=true' : ''}`);
export const createProductLabel = (payload: { value?: string; display_name: string; group_name?: string; sort_order?: number }) =>
    request<ApiProductLabelDefinition>('/product-labels', { method: 'POST', body: JSON.stringify(payload) });
export const updateProductLabel = (id: number, payload: Partial<Omit<ApiProductLabelDefinition, 'id'>>) =>
    request<ApiProductLabelDefinition>(`/product-labels/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });

export interface ApiProductLabelDeletePreview {
    label_id: number;
    display_name: string;
    is_active: boolean;
    affected_sku_count: number;
    ambiguous_display_name_count: number;
}

export interface ApiProductLabelDeleteResult {
    deleted: boolean;
    label_id: number;
    removed_from_sku_count: number;
    retained_ambiguous_display_name_count: number;
}

export const getProductLabelDeletePreview = (id: number) =>
    request<ApiProductLabelDeletePreview>(`/product-labels/${id}/delete-preview`);
export const deleteProductLabel = (id: number) =>
    request<ApiProductLabelDeleteResult>(`/product-labels/${id}`, { method: 'DELETE' });

export const createAttributeOption = (attrId: number, payload: { name: string }) =>
    request<ApiAttributeOption>(`/product-attributes/${attrId}/options`, { method: 'POST', body: JSON.stringify(payload) });
export const updateAttributeOption = (attrId: number, optId: number, payload: { name: string }) =>
    request<ApiAttributeOption>(`/product-attributes/${attrId}/options/${optId}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteAttributeOption = (attrId: number, optId: number) =>
    request<{ message: string }>(`/product-attributes/${attrId}/options/${optId}`, { method: 'DELETE' });

// ─── Inventory Locations ──────────────────────────────────────────────────────

export interface ApiInventoryLocationInfo {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    sort_order: number;
}

export const getInventoryLocations = () =>
    request<ApiInventoryLocationInfo[]>('/inventory-locations');

// ─── Image Tools ─────────────────────────────────────────────────────────────

export type CoverPosterSourceKind = 'original' | 'white_bg' | 'poster';
export type CoverPosterJobStatus =
    | 'queued'
    | 'uploading_input'
    | 'generating_white_bg'
    | 'white_bg_done'
    | 'generating_template'
    | 'template_done'
    | 'generating_cover'
    | 'completed'
    | 'failed';

export interface ApiCoverPosterJob {
    id: number;
    job_type: 'cover_poster' | 'product_poster';
    status: CoverPosterJobStatus;
    source_kind: CoverPosterSourceKind;
    headline_text: string;
    input_url: string | null;
    white_bg_url: string | null;
    template_url: string | null;
    final_poster_url: string | null;
    prompt_version: string | null;
    error_msg: string | null;
    identity_verification: ApiGeneratedImageIdentityVerification | null;
    created_at: string;
    updated_at: string;
}

export type GeneratedImageIdentityStatus =
    | 'preserved'
    | 'uncertain'
    | 'upstream_uncertain'
    | 'upstream_changed'
    | 'lifestyle_changed'
    | 'verification_unavailable';

export interface ApiGeneratedImageIdentityComparison {
    verdict: 'likely_preserved' | 'uncertain' | 'likely_changed';
    score: number;
    comparison_context: string;
    signals: Record<string, unknown>;
    reason_codes: string[];
    elapsed_ms?: number;
}

export interface ApiGeneratedImageIdentityVerification {
    status: GeneratedImageIdentityStatus;
    algorithm_version: string;
    comparisons?: {
        original_to_white_bg?: ApiGeneratedImageIdentityComparison;
        white_bg_to_lifestyle?: ApiGeneratedImageIdentityComparison;
    };
    reason_codes?: string[];
    diagnostic_code?:
        | 'dependency_unavailable'
        | 'image_processing_failed'
        | 'resource_exhausted'
        | 'internal_error';
}

export interface ApiCoverPosterJobList {
    items: ApiCoverPosterJob[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export async function createCoverPosterJob(payload: {
    photo: File;
    source_kind: CoverPosterSourceKind;
    headline_text?: string;
    template_x_offset?: number;
    template_y_offset?: number;
    template_scale?: number;
}): Promise<ApiCoverPosterJob> {
    const form = new FormData();
    form.append('photo', payload.photo);
    form.append('source_kind', payload.source_kind);
    if (payload.headline_text !== undefined) {
        form.append('headline_text', payload.headline_text);
    }
    if (payload.template_x_offset !== undefined) {
        form.append('template_x_offset', String(payload.template_x_offset));
    }
    if (payload.template_y_offset !== undefined) {
        form.append('template_y_offset', String(payload.template_y_offset));
    }
    if (payload.template_scale !== undefined) {
        form.append('template_scale', String(payload.template_scale));
    }

    const res = await erpFetch(`${BASE}/image-tools/cover-jobs`, {
        method: 'POST',
        body: form,
    });
    if (!res.ok) throw await apiError(res);
    return res.json();
}

export const listCoverPosterJobs = (params?: { page?: number; per_page?: number }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return request<ApiCoverPosterJobList>(`/image-tools/cover-jobs${qs ? `?${qs}` : ''}`);
};

export const getCoverPosterJob = (id: number) =>
    request<ApiCoverPosterJob>(`/image-tools/cover-jobs/${id}`);

export interface ApiProductPosterJob extends ApiCoverPosterJob {
    product_master_id: number | null;
}

export interface ApiLivestreamPosterJob extends ApiProductPosterJob {
    sku: string;
}

export interface ApiLivestreamPosterJobStatus {
    items: ApiLivestreamPosterJob[];
    summary: {
        total: number;
        active: number;
        completed: number;
        failed: number;
        review_required: number;
    };
}

export const getLivestreamPosterJobs = (livestreamId: number) =>
    request<ApiLivestreamPosterJobStatus>(`/livestreams/${livestreamId}/poster-jobs`);
export const peekLivestreamPosterJobs = (livestreamId: number, maxAge: number) =>
    peekQuery<ApiLivestreamPosterJobStatus>(`raw|${BASE}/livestreams/${livestreamId}/poster-jobs`, maxAge);

export const listProductPosters = (productMasterId: number, limit = 10) =>
    request<{ jobs: ApiProductPosterJob[] }>(
        `/products/${productMasterId}/posters?limit=${limit}`,
    );

export interface ApiProductPosterEnqueueResult {
    queued: number;
    skipped: number;
    failed: number;
    results: Array<{
        product_master_id: number;
        status: 'queued' | 'skipped' | 'failed';
        job_id?: number;
        reason_code?: string;
        reason?: string;
    }>;
}

export const enqueueProductPosters = (productMasterIds: number[]) =>
    request<ApiProductPosterEnqueueResult>('/products/posters/enqueue', {
        method: 'POST',
        body: JSON.stringify({ product_master_ids: productMasterIds }),
    });

export const retryProductPoster = (productMasterId: number, procurementLineId?: number) =>
    request<{ jobs: ApiProductPosterJob[] }>(
        `/products/${productMasterId}/posters/retry${productEditContext(procurementLineId)}`,
        { method: 'POST' },
    );
export const deleteCoverPosterJob = (id: number) =>
    request<{
        deleted: boolean;
        job_id: number;
        removed_livestream_references: number;
    }>(`/image-tools/cover-jobs/${id}`, { method: 'DELETE' });

export async function uploadProductImage(productMasterId: number, photo: File, assetKind: 'white_bg' | 'lifestyle' | 'poster', procurementLineId?: number) {
    const form = new FormData();
    form.append('photo', photo);
    form.append('asset_kind', assetKind);
    const res = await erpFetch(`${BASE}/products/${productMasterId}/posters/upload${productEditContext(procurementLineId)}`, {
        method: 'POST',
        body: form,
    });
    if (!res.ok) throw await apiError(res);
    return res.json() as Promise<{ asset_kind: typeof assetKind; url: string; job: ApiProductPosterJob | null }>;
}

// ─── Inventory ────────────────────────────────────────────────────────────────

export interface ApiInventoryLocation {
    warehouse_id: number;
    warehouse_name: string;
    warehouse_code?: string | null;
    location_id: number;
    location_name: string;
    location_code?: string | null;
    on_hand_quantity: number;
    reserved_live_quantity?: number;
    live_reserved_quantity?: number;
    shopify_reserved_quantity?: number;
    total_reserved_quantity?: number;
    available_quantity: number;
    incoming_quantity: number;
}

export type ApiSourceImageKind = 'product_photo' | 'poster';
export type ApiBottomImageDisposition = 'captured' | 'not_applicable' | null;

export interface ApiProductImageAsset {
    id: number | null;
    product_master_id?: number;
    role: 'primary' | 'bottom' | 'other' | string;
    source_url?: string | null;
    url?: string | null;
    status?: string | null;
    error?: string | null;
    sort_order?: number;
    legacy?: boolean;
}

export interface ApiProductImages {
    source_original_url?: string | null;
    original_url?: string | null;
    white_bg_url?: string | null;
    lifestyle_url?: string | null;
    poster_url?: string | null;
    bottom_url?: string | null;
}

export interface ApiProductCategorySummary {
    id: number;
    name: string;
}

export interface ApiProductSupplierAssociation {
    id: number;
    product_master_id: number;
    supplier_id: number;
    supplier_name: string | null;
    supplier_sku: string | null;
}

export interface ApiProductSummary {
    id: number;
    sku: string;
    name?: string | null;
    display_name?: string | null;
    purchase_url?: string | null;
    images?: ApiProductImages;
    // Medium, Qiniu-resized signed URLs for overview cards/list thumbnails.
    previews?: ApiProductImages;
    // Small, Qiniu-resized signed URLs (same keys as `images`) for list/grid
    // thumbnails — much smaller payload than the full-res `images`.
    thumbnails?: ApiProductImages;
    height?: number | null;
    width?: number | null;
    depth?: number | null;
    internal_height?: number | null;
    internal_width?: number | null;
    internal_depth?: number | null;
    weight?: number | null;
    unit?: string | null;
    category_id?: number | null;
    category?: ApiProductCategorySummary | null;
    product_labels?: string[];
    product_note?: string | null;
    product_note_summary?: string | null;
    aisle?: number | null;
    bin?: number | null;
    source_original_url?: string | null;
    source_image_kind?: ApiSourceImageKind | null;
    original_image_status?: string | null;
    original_image_error?: string | null;
    bottom_image_disposition?: ApiBottomImageDisposition;
    image_assets?: ApiProductImageAsset[];
    attributes?: Record<string, string[]>;
    attribute_values?: Array<{
        id: number;
        product_master_id: number;
        attribute_id: number;
        attribute_key?: string | null;
        attribute_name?: string | null;
        option_id: number | null;
        option_name?: string | null;
    }>;
    supplier_associations?: ApiProductSupplierAssociation[];
}

export interface ApiInventoryItem {
    id: number;
    product_master_id: number;
    sku: string;
    product_name: string;
    on_hand_quantity: number;
    reserved_live_quantity: number;
    live_reserved_quantity: number;
    shopify_reserved_quantity: number;
    total_reserved_quantity: number;
    available_quantity: number;
    incoming_quantity: number;
    stock_state: string;
    sea_base_cost?: number | null;
    air_base_cost?: number | null;
    base_cost_currency?: string | null;
    locations: ApiInventoryLocation[];
    product_master?: ApiProductSummary;
}

export interface ApiInventoryList {
    items: ApiInventoryItem[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export const getInventory = (params?: {
    q?: string;
    location_id?: number;
    warehouse_id?: number;
    stock_state?: string;
    page?: number;
    per_page?: number;
}, options?: { signal?: AbortSignal }) => {
    const q = new URLSearchParams({ view: 'overview' });
    if (params?.q) q.set('q', params.q);
    if (params?.location_id) q.set('location_id', String(params.location_id));
    if (params?.warehouse_id) q.set('warehouse_id', String(params.warehouse_id));
    if (params?.stock_state) q.set('stock_state', params.stock_state);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return request<ApiInventoryList>(`/inventory${qs ? `?${qs}` : ''}`, options);
};

export interface ApiInventoryDetail {
    id: number;
    product_master_id: number;
    sku: string;
    product_name: string;
    on_hand_quantity: number;
    available_quantity: number;
    reserved_live_quantity: number;
    live_reserved_quantity: number;
    shopify_reserved_quantity: number;
    total_reserved_quantity: number;
    incoming_quantity: number;
    stock_state: string;
    sea_base_cost?: number | null;
    air_base_cost?: number | null;
    base_cost_currency?: string | null;
    purchase_price?: number | null;
    purchase_currency?: string | null;
    locations: ApiInventoryLocation[];
    product_master: ApiProductSummary;
    procurement_incoming_lines: Array<{
        id: number;
        target_quantity: number;
        received_quantity: number;
        procurement_order: { id: number; name: string };
    }>;
    live_reservations: Array<{
        id: number;
        reserved_quantity: number;
        sold_quantity: number;
        livestream: { id: number; name: string; status: string };
    }>;
    shopify_product?: ApiShopifyProduct | null;
    movement_history: Array<{
        id: number;
        movement_type: string;
        quantity_change: number;
        created_at: string;
        notes?: string;
    }>;
}

export const getInventoryDetail = (productMasterId: number, options?: { signal?: AbortSignal }) =>
    request<ApiInventoryDetail>(`/inventory/${productMasterId}`, { signal: options?.signal });

export interface ApiCatalogProductSummary {
    id: number;
    sku: string;
    display_name?: string | null;
    images?: ApiProductImages;
    previews?: ApiProductImages;
    thumbnails?: ApiProductImages;
    height?: number | null;
    width?: number | null;
    depth?: number | null;
    internal_height?: number | null;
    internal_width?: number | null;
    internal_depth?: number | null;
    weight?: number | null;
    opening_diameter?: number | null;
    unit?: string | null;
    category?: ApiProductCategorySummary | null;
    attributes?: Record<string, string[]>;
    attribute_values?: ApiProductSummary['attribute_values'];
}

export interface ApiCatalogItem {
    id: number;
    product_master_id: number;
    sku: string;
    product_name: string;
    product_master?: ApiCatalogProductSummary | null;
}

export interface ApiCatalogList {
    items: ApiCatalogItem[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export const getCustomerCatalog = (params?: {
    q?: string;
    attribute_option_id?: number;
    page?: number;
    per_page?: number;
}) => {
    const q = new URLSearchParams();
    if (params?.q) q.set('q', params.q);
    if (params?.attribute_option_id) q.set('attribute_option_id', String(params.attribute_option_id));
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return request<ApiCatalogList>(`/catalog${qs ? `?${qs}` : ''}`);
};

export interface ApiShopifyProduct {
    id: number;
    master_id: number;
    sku?: string | null;
    title?: string | null;
    poster_url?: string | null;
    listing_price?: number | null;
    listing_currency: string;
    quantity: number;
    inventory_location_id: number;
    inventory_warehouse_id: number;
    listing_status: string;
}

export interface ApiShopifyAllocationResult {
    product_master_id: number;
    sku: string;
    balance: ApiInventoryLocation;
    movement: {
        id: number;
        movement_type: string;
        on_hand_delta: number;
        reserved_delta: number;
        created_at: string;
    };
    shopify_product: ApiShopifyProduct | null;
    inventory_detail: ApiInventoryDetail;
}

export const allocateShopifyInventory = (payload: {
    product_master_id: number;
    warehouse_id: number;
    quantity: number;
    listing_price?: number;
    listing_currency?: string;
}) => request<ApiShopifyAllocationResult>('/inventory/shopify-allocations', {
    method: 'POST',
    body: JSON.stringify(payload),
});

export const releaseShopifyInventory = (payload: {
    product_master_id: number;
    quantity?: number;
}) => request<ApiShopifyAllocationResult>('/inventory/shopify-allocations/release', {
    method: 'POST',
    body: JSON.stringify(payload),
});

export const createInventoryAdjustment = (payload: {
    product_master_id: number;
    warehouse_id: number;
    on_hand_delta?: number;
    reserved_delta?: number;
    notes?: string;
}) => request<ApiInventoryDetail>('/inventory/adjustments', { method: 'POST', body: JSON.stringify(payload) });

export type ApiStockUpdateField = 'sku' | 'quantity' | 'location_id' | 'location_code';
export type ApiStockUpdateMappings = Partial<Record<ApiStockUpdateField, string>>;

export interface ApiStockUpdateRowError {
    type: string;
    message: string;
}

export interface ApiStockUpdatePreviewRow {
    row_number: number;
    sku: string | null;
    quantity: number | null;
    location_id: number | null;
    location_code: string | null;
    location_name: string | null;
    product_master_id: number | null;
    balance_exists: boolean;
    previous_on_hand_quantity: number;
    reserved_quantity: number;
    reserved_live_quantity: number;
    shopify_reserved_quantity: number;
    on_hand_delta: number | null;
    valid: boolean;
    errors: ApiStockUpdateRowError[];
    raw_values: Record<string, unknown>;
}

export interface ApiStockUpdatePreview {
    filename: string | null;
    sheet_name: string | null;
    detected_columns: string[];
    proposed_mappings: ApiStockUpdateMappings;
    ignored_columns: string[];
    row_count: number;
    valid_count: number;
    error_count: number;
    row_previews: ApiStockUpdatePreviewRow[];
}

export interface ApiStockUpdateResultRow {
    row_number: number;
    master: ApiProductSummary;
    inventory: {
        location_id: number;
        location_code: string;
        location_name: string;
        previous_on_hand_quantity: number;
        updated_on_hand_quantity: number;
        reserved_quantity: number;
        reserved_live_quantity: number;
        shopify_reserved_quantity: number;
        on_hand_delta: number;
    };
}

export interface ApiStockUpdateCommitResult {
    source_id: string;
    filename: string | null;
    sheet_name: string | null;
    row_count: number;
    updated_count: number;
    inventory_movement_count: number;
    results: ApiStockUpdateResultRow[];
}

export async function previewStockUpdate(payload: {
    file: File;
    mappings?: ApiStockUpdateMappings;
    sheetName?: string;
    defaultLocationId?: number;
    defaultWarehouseId?: number;
}): Promise<ApiStockUpdatePreview> {
    const form = new FormData();
    form.append('file', payload.file);
    if (payload.mappings) form.append('mappings', JSON.stringify(payload.mappings));
    if (payload.sheetName) form.append('sheet_name', payload.sheetName);
    if (payload.defaultLocationId) form.append('default_warehouse_id', String(payload.defaultLocationId));
    if (payload.defaultWarehouseId) form.append('default_warehouse_id', String(payload.defaultWarehouseId));
    const response = await erpFormRequest<{ success: boolean; data: ApiStockUpdatePreview }>('/inventory/stock-updates/preview', form);
    return response.data;
}

export async function commitStockUpdate(payload: {
    file: File;
    mappings?: ApiStockUpdateMappings;
    sheetName?: string;
    defaultLocationId?: number;
    defaultWarehouseId?: number;
}): Promise<ApiStockUpdateCommitResult> {
    const form = new FormData();
    form.append('file', payload.file);
    if (payload.mappings) form.append('mappings', JSON.stringify(payload.mappings));
    if (payload.sheetName) form.append('sheet_name', payload.sheetName);
    if (payload.defaultLocationId) form.append('default_warehouse_id', String(payload.defaultLocationId));
    if (payload.defaultWarehouseId) form.append('default_warehouse_id', String(payload.defaultWarehouseId));
    const response = await erpFormRequest<{ success: boolean; data: ApiStockUpdateCommitResult }>('/inventory/stock-updates/commit', form);
    return response.data;
}

export interface ApiImageEmbeddingProcessResult {
    enqueued: number;
    scanned: number;
    ready: number;
    failed: number;
    skipped: number;
    dry_run: number;
    limit: number;
    retry_failed: boolean;
}

export const processInventoryImageEmbeddings = (payload?: {
    limit?: number;
    retry_failed?: boolean;
}) => request<ApiImageEmbeddingProcessResult>('/inventory/image-embeddings/process', {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
});

export interface ApiImportedOriginalStatusCounts {
    missing_original_url: number;
    pending: number;
    prepared: number;
    failed: number;
}

export interface ApiImportedOriginalProcessResult {
    scanned: number;
    prepared: number;
    failed: number;
    skipped: number;
    dry_run: number;
    limit?: number;
    retry_failed?: boolean;
    product_master_id?: number;
    counts: ApiImportedOriginalStatusCounts;
}

export const getImportedOriginalStatus = () =>
    request<ApiImportedOriginalStatusCounts>('/inventory/image-originals/status');

export const processImportedOriginalImages = (payload?: {
    limit?: number;
    retry_failed?: boolean;
    product_master_ids?: number[];
}) => request<ApiImportedOriginalProcessResult>('/inventory/image-originals/process', {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
});

export const retryImportedOriginalImage = (productMasterId: number, procurementLineId?: number) =>
    request<ApiImportedOriginalProcessResult>(
        `/inventory/${productMasterId}/image-original/retry${productEditContext(procurementLineId)}`,
        { method: 'POST' },
    );

// ─── Product Masters / Imports ───────────────────────────────────────────────

export type ApiProductImportField =
    | 'sku'
    | 'original_url'
    | 'bottom_image_url'
    | 'other_image_urls'
    | 'bottom_not_applicable'
    | 'purchase_url'
    | 'product_labels'
    | 'source_image_kind'
    | 'height'
    | 'width'
    | 'depth'
    | 'internal_height'
    | 'internal_width'
    | 'internal_depth'
    | 'weight'
    | 'opening_diameter'
    | 'unit'
    | 'category_id'
    | 'aisle'
    | 'bin_number'
    | 'quantity'
    | 'location_id'
    | 'location_code'
    | 'purchase_price'
    | 'supplier'
    | 'supplier_sku'
    | 'product_attributes';

export type ApiProductImportMappings = Partial<Record<ApiProductImportField, string>>;

export interface ApiProductImportWarning {
    type: string;
    row_number?: number;
    message: string;
}

export interface ApiProductImportPreviewRow {
    row_number: number;
    sku: string | null;
    action: 'create' | 'update';
    mapped_values: Partial<Record<ApiProductImportField | `attribute:${string}`, unknown>>;
    raw_values: Record<string, unknown>;
    embedded_images?: {
        primary_count: number;
        bottom_count: number;
        other_count: number;
        overridden_url_fields: ApiProductImportField[];
    };
}

export interface ApiProductImportPreview {
    filename: string | null;
    sheet_name: string | null;
    detected_columns: string[];
    proposed_mappings: ApiProductImportMappings;
    ignored_columns: string[];
    row_count: number;
    row_previews: ApiProductImportPreviewRow[];
    warnings: ApiProductImportWarning[];
}

export interface ApiProductImportResultRow {
    row_number: number;
    created: boolean;
    updated_fields: string[];
    master: ApiProductSummary;
    inventory: {
        location_id: number;
        location_code: string;
        location_name: string;
        previous_on_hand_quantity: number;
        imported_on_hand_quantity: number;
        on_hand_delta: number;
    } | null;
    procurement_line?: {
        order_id: number;
        order_code: string;
        line_id: number;
        purchase_price: number;
        purchase_currency: string;
        supplier_id: number | null;
        sea_base_cost: number | null;
        air_base_cost: number | null;
    } | null;
}

export interface ApiProductImportCommitResult {
    source_id: string;
    filename: string | null;
    sheet_name: string | null;
    row_count: number;
    created_count: number;
    updated_count: number;
    inventory_movement_count: number;
    image_prepare_queued_count?: number;
    image_prepare_status_counts?: ApiImportedOriginalStatusCounts;
    results: ApiProductImportResultRow[];
}

export interface ApiProductMasterUpsertResult extends ApiProductImportResultRow {}

export async function previewProductImport(
    file: File,
    mappings?: ApiProductImportMappings,
    sheetName?: string
): Promise<ApiProductImportPreview> {
    const form = new FormData();
    form.append('file', file);
    if (sheetName) form.append('sheet_name', sheetName);
    if (mappings && Object.keys(mappings).length > 0) form.append('mappings', JSON.stringify(mappings));
    const response = await productFormRequest<{ success: boolean; data: ApiProductImportPreview }>('/imports/preview', form);
    return response.data;
}

export async function commitProductImport(payload: {
    file: File;
    mappings?: ApiProductImportMappings;
    sheetName?: string;
    defaultLocationId?: number;
    defaultSourceImageKind?: ApiSourceImageKind;
    defaultCategoryId?: number;
    defaultSupplierId?: number;
}): Promise<ApiProductImportCommitResult> {
    const form = new FormData();
    form.append('file', payload.file);
    if (payload.mappings) form.append('mappings', JSON.stringify(payload.mappings));
    if (payload.sheetName) form.append('sheet_name', payload.sheetName);
    if (payload.defaultLocationId) form.append('default_location_id', String(payload.defaultLocationId));
    if (payload.defaultSourceImageKind) form.append('default_source_image_kind', payload.defaultSourceImageKind);
    if (payload.defaultCategoryId) form.append('default_category_id', String(payload.defaultCategoryId));
    if (payload.defaultSupplierId) form.append('default_supplier_id', String(payload.defaultSupplierId));
    const response = await productFormRequest<{ success: boolean; data: ApiProductImportCommitResult }>('/imports/commit', form);
    return response.data;
}

export const createOrUpdateProductMaster = (payload: {
    sku?: string;
    original_url?: string;
    bottom_image_url?: string;
    other_image_urls?: string[];
    bottom_not_applicable?: boolean;
    purchase_url?: string;
    product_note?: string;
    source_image_kind?: ApiSourceImageKind;
    photo?: File;
    bottom_photo?: File;
    other_photos?: File[];
    height?: number | null;
    width?: number | null;
    depth?: number | null;
    internal_height?: number | null;
    internal_width?: number | null;
    internal_depth?: number | null;
    weight?: number | null;
    opening_diameter?: number | null;
    unit?: string;
    category_id?: number;
    product_labels?: string[];
    purchase_price?: number;
    supplier_id?: number;
    supplier_sku?: string;
    attribute_option_ids?: number[];
    attribute_ids?: number[];
    inventory?: {
        quantity: number;
        location_id?: number;
        location_code?: string;
    };
}): Promise<ApiProductMasterUpsertResult> => {
    if (payload.photo) {
        const form = new FormData();
        form.append('photo', payload.photo);
        if (payload.bottom_photo) form.append('bottom_photo', payload.bottom_photo);
        payload.other_photos?.forEach(photo => form.append('other_photos', photo));
        if (payload.sku) form.append('sku', payload.sku);
        if (payload.original_url) form.append('original_url', payload.original_url);
        if (payload.bottom_image_url) form.append('bottom_image_url', payload.bottom_image_url);
        if (payload.other_image_urls) form.append('other_image_urls', JSON.stringify(payload.other_image_urls));
        if (payload.bottom_not_applicable !== undefined) form.append('bottom_not_applicable', String(payload.bottom_not_applicable));
        if (payload.purchase_url !== undefined) form.append('purchase_url', payload.purchase_url);
        if (payload.product_note !== undefined) form.append('product_note', payload.product_note);
        if (payload.product_labels) form.append('product_labels', JSON.stringify(payload.product_labels));
        if (payload.source_image_kind) form.append('source_image_kind', payload.source_image_kind);
        if (payload.height != null) form.append('height', String(payload.height));
        if (payload.width != null) form.append('width', String(payload.width));
        if (payload.depth != null) form.append('depth', String(payload.depth));
        if (payload.internal_height != null) form.append('internal_height', String(payload.internal_height));
        if (payload.internal_width != null) form.append('internal_width', String(payload.internal_width));
        if (payload.internal_depth != null) form.append('internal_depth', String(payload.internal_depth));
        if (payload.weight != null) form.append('weight', String(payload.weight));
        if (payload.opening_diameter != null) form.append('opening_diameter', String(payload.opening_diameter));
        if (payload.unit) form.append('unit', payload.unit);
        if (payload.category_id != null) form.append('category_id', String(payload.category_id));
        if (payload.purchase_price != null) form.append('purchase_price', String(payload.purchase_price));
        if (payload.supplier_id != null) form.append('supplier_id', String(payload.supplier_id));
        if (payload.supplier_sku !== undefined) form.append('supplier_sku', payload.supplier_sku);
        if (payload.attribute_ids) form.append('attribute_ids', JSON.stringify(payload.attribute_ids));
        if (payload.attribute_option_ids) form.append('attribute_option_ids', JSON.stringify(payload.attribute_option_ids));
        if (payload.inventory) {
            form.append('quantity', String(payload.inventory.quantity));
            if (payload.inventory.location_id != null) form.append('location_id', String(payload.inventory.location_id));
            if (payload.inventory.location_code) form.append('location_code', payload.inventory.location_code);
        }
        return productFormRequest<{ success: boolean; data: ApiProductMasterUpsertResult }>('/masters', form)
            .then(response => response.data);
    }
    return productJsonRequest<{ success: boolean; data: ApiProductMasterUpsertResult }>('/masters', {
        method: 'POST',
        body: JSON.stringify(payload),
    }).then(response => response.data);
};

// ─── Livestreams ──────────────────────────────────────────────────────────────

// 直播三态（+ 已取消）：待开播 pending（编排/推送采购）· 直播中 live（开始贴标后）·
// 已结束 completed（完成直播后）· 已取消 cancelled。
export type LivestreamStatus = 'pending' | 'live' | 'completed' | 'cancelled';

export interface ApiLivestreamStoryMetadata {
    // TODO: Future deprecation. Backend keeps historical story labels for
    // compatibility, but new frontend surfaces should hide this field.
    label?: string;
    page_break_before?: boolean;
    [key: string]: unknown;
}

export interface ApiLivestreamSelectionMetadata {
    tags: string[];
    ai_plan?: {
        item_role?: string;
        topic_segment?: string;
        short_reason?: string;
        comment?: string;
        [key: string]: unknown;
    };
    story?: ApiLivestreamStoryMetadata;
    [key: string]: unknown;
}

// A 采购单 that contains this product at the live's location (informational
// provenance; a product can have several sources).
export interface ApiProcurementSource {
    procurement_order_id: number;
    order_name: string | null;
    order_code: string;
    order_status: string;
    line_status: string;
    stage: ItemReadiness;
    target_quantity: number;
    purchased_quantity: number;
    received_quantity: number;
    // Optional 采购数量限制 (lower/upper bound) to guide the live quantity.
    min_purchase_quantity: number | null;
    max_purchase_quantity: number | null;
    supplier_name: string | null;
}

export interface ApiLivestreamItem {
    id: number;
    product_master_id: number;
    group_id?: number | null; // per-直播 分组 (ErpLivestreamGroup). null = 未分组.
    reserved_quantity: number;
    sold_quantity: number;
    sort_order: number;
    custom_sort_order: number;
    source?: 'product_library' | 'procurement' | 'order_return';
    product_name: string;
    product_master?: ApiProductSummary;
    suggested_price?: number | null;
    suggested_price_currency?: string | null;
    suggested_price_source?: string | null;
    final_listing_price?: number | null;
    final_listing_price_currency?: string | null;
    final_buy_now_price?: number | null;
    final_buy_now_price_currency?: string | null;
    live_base_cost?: number | null;
    live_base_cost_currency?: string | null;
    live_base_cost_source?: string | null;
    live_recommended_auction_price?: number | null;
    live_recommended_buy_now_price?: number | null;
    live_recommended_price_currency?: string | null;
    live_recommended_price_source?: string | null;
    estimated_profit_amount?: number | null;
    estimated_margin_percentage?: number | null;
    system_standard_sale_price?: number | null;
    standard_sale_price_currency?: string | null;
    target_purchase_price_0_margin?: Record<string, ApiPurchaseBudget>;
    target_purchase_price_profit?: Record<string, Pick<ApiPurchaseBudget, 'raw_target_purchase_price' | 'display_target_purchase_price' | 'pricing_status'>>;
    target_profit_usd?: number | null;
    supplier_purchase_price?: number | null;
    supplier_purchase_currency?: string | null;
    expected_profit?: number | null;
    expected_margin_rate?: number | null;
    price_gap_to_break_even?: number | null;
    pricing_status?: 'normal' | 'low_purchase_budget' | 'sale_price_below_cost_floor' | 'missing_measurements';
    pricing_breakdown?: Record<string, unknown>;
    pricing_rule_version?: string | null;
    selected_route?: 'sea' | 'air';
    missing_fields?: string[];
    pricing_snapshot?: Record<string, unknown> | null;
    selection_metadata?: ApiLivestreamSelectionMetadata;
    supplier_id?: number | null;
    supplier_name?: string | null;
    supplier_brand_story?: string | null;
    // 预排 readiness, relative to this item's claim at the live's location.
    reserved_fulfilled_quantity?: number;
    shipped_quantity?: number;
    readiness?: ItemReadiness;
    location_on_hand_quantity: number;
    location_available_quantity?: number;
    location_incoming_quantity?: number;
    planned_shortfall_quantity?: number;
    outstanding_quantity?: number;
    // 在库 shortfall (claim minus on-hand backing); stock_warning is true only
    // inside the pre-live window when this item's claim exceeds 在库 stock.
    stock_shortfall_quantity?: number;
    stock_warning?: boolean;
    // Source 采购单(s) this product appears in (provenance, may be several).
    procurement_sources?: ApiProcurementSource[];
}

export interface ApiPurchaseBudget {
    raw_target_purchase_price: number;
    display_target_purchase_price: number | null;
    required_sale_price: number;
    sale_price_gap: number;
    pricing_status: string;
    allowed_landed_cost_usd: number;
    fixed_cost_rmb: number;
}

// One 场次 (airing) of a livestream: its own date/time + 主播 + 导播 + 回传状态.
export interface ApiLivestreamSession {
    id: number;
    livestream_id: number;
    scheduled_at: string | null;
    streamer_id: number | null;
    streamer_name: string | null;
    director_id: number | null;
    director_name: string | null;
    returned_at: string | null;
    is_returned: boolean;
    returned_import_id: number | null;
    shipped_at: string | null;
    is_shipped: boolean;
    sort_order: number;
}

// Input shape for creating/updating a 场次 inline via the livestream payload.
export interface LivestreamSessionInput {
    id?: number;
    scheduled_at: string;
    streamer_id?: number | null;
    director_id?: number | null;
}

export interface ApiLivestreamGroup {
    id: number;
    livestream_id: number;
    name: string;
    description?: string | null;
    sort_order: number;
    item_count: number;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface ApiLivestreamCover {
    item_id: number;
    product_master_id: number;
    product_name: string;
    sku: string | null;
    source_kind: CoverPosterSourceKind;
    source_image_url: string | null;
    job: ApiCoverPosterJob | null;
    final_poster_url: string | null;
    is_uploaded?: boolean;
}

export interface ApiLivestream {
    id: number;
    live_code: string;
    item_sort_mode?: LivestreamItemSortMode;
    unit_order?: ApiLivestreamUnit[];
    groups?: ApiLivestreamGroup[]; // per-直播 分组, empty for a fresh live.
    name: string;
    description: string;
    product_pitch_script?: string | null;
    sessions?: ApiLivestreamSession[];
    streamer_id: number | null;
    streamer_name: string;
    live_type: string;
    status: LivestreamStatus;
    scheduled_at: string | null;
    location_id: number | null;
    location_code: string | null;
    location_name: string | null;
    warehouse_id: number;
    warehouse_code: string | null;
    warehouse_name: string | null;
    shipping_adjustment_usd: number;
    item_count: number;
    reserved_quantity: number;
    sold_quantity: number;
    // True when, within ~1 week of scheduled_at, some item's claim exceeds 在库 stock.
    has_stock_warning?: boolean;
    // True once a 采购单 has been generated (推送采购单 / 开始贴标). Copy/move items is
    // only allowed before this.
    has_generated_procurement?: boolean;
    final_price_item_count?: number;
    priced_item_count?: number;
    margin_item_count?: number;
    estimated_revenue?: number | null;
    estimated_cost?: number | null;
    estimated_profit_amount?: number | null;
    estimated_margin_percentage?: number | null;
    actual_revenue?: number | null;
    actual_cost?: number | null;
    actual_profit_amount?: number | null;
    actual_margin_percentage?: number | null;
    actual_sold_quantity?: number;
    actual_order_count?: number;
    actual_line_count?: number;
    actual_import_status?: string | null;
    latest_order_import?: ApiLivestreamOrderImportSummary | null;
    order_imports?: ApiLivestreamOrderImportSummary[];
    items?: ApiLivestreamItem[];
    settled_at?: string | null;
    // Set once this live's 未售 leftover has been released to another live (one-time).
    unsold_released_at?: string | null;
    unsold_released_to_id?: number | null;
    is_settled?: boolean;
    created_at: string;
    updated_at: string;
}

export interface ApiLivestreamOrderImportSummary {
    id: number;
    platform: string;
    filename: string | null;
    selected_sheet: string;
    status: string;
    row_count: number;
    matched_count: number;
    error_count: number;
    total_quantity: number;
    total_revenue: number | null;
    currency: string | null;
    committed_at: string | null;
    created_at: string | null;
}

export interface ApiLivestreamOrderImportRow {
    append_to_live?: boolean;
    row_number?: number;
    row_numbers?: number[];
    order_numbers?: string[];
    order_number?: string | null;
    order_date?: string | null;
    platform_username?: string | null;
    recipient_name?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    postal_code?: string | null;
    normalized_sku?: string | null;
    sku?: string | null;
    uploaded_sku?: string | null;
    raw_item_title?: string | null;
    quantity?: number;
    unit_sale_price?: string;
    currency?: string | null;
    order_status?: string | null;
    livestream_item_id?: number | null;
    product_master_id?: number;
    product_name?: string | null;
    reserved_quantity?: number;
    current_sold_quantity?: number;
    error_kind?: string;
    error?: string;
    existing_line_id?: number;
    previous_quantity?: number;
    previous_unit_sale_price?: string | null;
}

export interface ApiLivestreamOrderImportItemEffect {
    livestream_item_id: number;
    product_master_id: number;
    sku?: string | null;
    product_name?: string | null;
    current_sold_quantity: number;
    proposed_sold_quantity: number;
    sold_delta: number;
    reserved_quantity: number;
}

export interface ApiLivestreamOrderImportPreview {
    import: ApiLivestreamOrderImportSummary;
    valid: boolean;
    matched_rows: ApiLivestreamOrderImportRow[];
    new_rows: ApiLivestreamOrderImportRow[];
    updated_rows: ApiLivestreamOrderImportRow[];
    unchanged_rows: ApiLivestreamOrderImportRow[];
    unmatched_rows: ApiLivestreamOrderImportRow[];
    duplicate_rows: ApiLivestreamOrderImportRow[];
    invalid_rows: ApiLivestreamOrderImportRow[];
    non_usd_rows: ApiLivestreamOrderImportRow[];
    quantity_over_reserved_rows: ApiLivestreamOrderImportRow[];
    below_current_sold_rows: ApiLivestreamOrderImportRow[];
    affected_item_summaries: ApiLivestreamOrderImportItemEffect[];
    totals: {
        row_count: number;
        matched_count: number;
        error_count: number;
        total_quantity: number;
        total_revenue: number | null;
        currency: string | null;
    };
}

export interface ApiLivestreamOrderImportCommitResult {
    import: ApiLivestreamOrderImportSummary;
    livestream: ApiLivestream;
    summary_email_task?: {
        id: number;
        status: string;
        created: boolean;
    };
}

export interface ApiLivestreamPlanImportRow {
    row_number: number;
    sku: string;
    normalized_sku: string;
    run_order: number | null;
    quantity: number;
    cover_candidate: boolean;
    item_role: string;
    topic_segment: string;
    group_name: string;
    short_reason: string;
    comment: string;
    product_master_id: number | null;
    product: ApiSelectableItem | null;
    errors: string[];
    warnings: string[];
}

export interface ApiLivestreamPlanImportPreview {
    valid: boolean;
    livestream_id: number;
    rows: ApiLivestreamPlanImportRow[];
    valid_rows: ApiLivestreamPlanImportRow[];
    invalid_rows: ApiLivestreamPlanImportRow[];
    totals: {
        row_count: number;
        valid_count: number;
        invalid_count: number;
        quantity_total: number;
        cover_candidate_count: number;
    };
    groups: Array<{ name: string; item_count: number; exists: boolean }>;
}

export interface ApiLivestreamPlanImportCommitResult {
    livestream: ApiLivestream;
    added_count: number;
    updated_count: number;
    metadata_count: number;
    reordered_count: number;
    cover_item_id: number | null;
}

export interface ApiLivestreamList {
    items: ApiLivestream[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export const getLivestreams = (params?: {
    status?: string;
    live_type?: string;
    q?: string;
    sort?: string;
    page?: number;
    per_page?: number;
}, opts?: { signal?: AbortSignal }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.live_type) q.set('live_type', params.live_type);
    if (params?.q) q.set('q', params.q);
    if (params?.sort) q.set('sort', params.sort);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return request<ApiLivestreamList>(`/livestreams${qs ? `?${qs}` : ''}`, { signal: opts?.signal });
};

export const createLivestream = (payload: {
    name?: string;
    description?: string;
    product_pitch_script?: string;
    streamer_id?: number;
    live_type: string;
    warehouse_id: number;
    shipping_adjustment_usd?: number;
    scheduled_at?: string;
    status?: string;
    sessions?: LivestreamSessionInput[];
}) => request<ApiLivestream>('/livestreams', { method: 'POST', body: JSON.stringify(payload) });

export interface ApiLivestreamStock {
    id: number;
    status: ApiLivestream['status'];
    is_settled: boolean;
    items: Pick<ApiLivestreamItem, 'id' | 'reserved_quantity' | 'reserved_fulfilled_quantity' | 'sold_quantity' | 'shipped_quantity' | 'location_available_quantity'>[];
}
export const getLivestreamStock = (id: number, signal?: AbortSignal) =>
    request<ApiLivestreamStock>(`/livestreams/${id}/stock`, { signal });

export const getLivestream = (id: number, options?: { signal?: AbortSignal; view?: 'workspace' | 'selection' }) =>
    request<ApiLivestream>(`/livestreams/${id}${options?.view ? `?view=${options.view}` : ''}`, { signal: options?.signal });
export const cacheLivestream = (live: ApiLivestream) => putQuery(`raw|${BASE}/livestreams/${live.id}?view=workspace`, live);
export const getCachedLivestream = (id: number, maxAge?: number) =>
    peekQuery<ApiLivestream>(`raw|${BASE}/livestreams/${id}?view=workspace`, maxAge);

export interface LivestreamSummaryEmailTaskResult {
    task: {
        id: number;
        status: string;
        content_hash: string;
        subject: string;
    };
    created: boolean;
    content_hash: string;
}

export const enqueueLivestreamSummaryEmail = (id: number) =>
    request<LivestreamSummaryEmailTaskResult>(
        `/livestreams/${id}/summary-email-tasks`,
        { method: 'POST' },
    );

export const updateLivestream = (id: number, payload: Partial<{
    name: string;
    description: string;
    product_pitch_script: string;
    streamer_id: number;
    live_type: string;
    location_id: number;
    warehouse_id: number;
    status: string;
    scheduled_at: string;
    shipping_adjustment_usd: number;
    sessions: LivestreamSessionInput[];
}>) =>
    request<ApiLivestream>(`/livestreams/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });

// Mark one 场次 as 回传'ed (locks its date/host/director) or clear it.
export const setLivestreamSessionReturned = (liveId: number, sessionId: number, returned: boolean) =>
    request<ApiLivestream>(`/livestreams/${liveId}/sessions/${sessionId}/returned`, {
        method: returned ? 'POST' : 'DELETE',
    });

// Duplicate a not-yet-confirmed 直播预排 (items + 草稿采购单 links + settings).
// Does not change any 采购单 目标数. Returns the new livestream.
export const duplicateLivestream = (id: number) =>
    request<ApiLivestream>(`/livestreams/${id}/duplicate`, { method: 'POST' });

// Live-first procurement: shortfall preview + one-step 开始贴标 that reserves stock and
// generates the shortfall 采购单. See docs/live-first-procurement.md.
export interface ApiProcurementPreviewItem {
    product_master_id: number;
    sku: string | null;
    product_name: string | null;
    demand: number;
    available: number;
    shortfall: number;
}
export interface ApiProcurementPreview {
    livestream_id: number;
    shortfall_items: ApiProcurementPreviewItem[];
    will_generate_order: boolean;
    // 库存未覆盖的真实缺口（含已在采购单上、仍在采购/在途的部分）。用于区分
    // 「全部由现货满足」(0) 与「剩余需求已在采购单中」(>0)。
    outstanding_shortfall?: number;
    /** 其中确定到不了的部分：供应商回绝或接单不足，按规则不会再自动补单。 */
    unfillable_shortfall?: number;
    unfillable_items?: {
        product_master_id: number;
        sku: string | null;
        product_name: string | null;
        missing: number;
    }[];
}
export const getLivestreamProcurementPreview = (id: number) =>
    request<ApiProcurementPreview>(`/livestreams/${id}/procurement-preview`);

export type ApiLivestreamProcurementResult = ApiLivestream & {
    generated_order: { order_code: string; line_count: number } | null;
    warnings?: string[];
};

// 推送采购单 (repeatable, pre-confirm): reserve stock + create/reconcile the shortfall
// 采购单 without locking the live. See docs/live-first-procurement.md §6b.
export const pushLivestreamProcurement = (id: number) =>
    request<ApiLivestreamProcurementResult>(
        `/livestreams/${id}/push-procurement`, { method: 'POST' },
    );

export const confirmLivestreamGenerate = (id: number) =>
    request<ApiLivestreamProcurementResult>(
        `/livestreams/${id}/confirm-generate`, { method: 'POST' },
    );

export const settleLivestream = (id: number, finalize = false) =>
    request<{ livestream: ApiLivestream; items: ApiLivestreamItem[] }>(`/livestreams/${id}/${finalize ? 'confirm-orders-returned' : 'complete'}`, {
        method: 'POST',
        body: JSON.stringify({ finalize }),
    });

export interface ApiReleaseCandidate {
    id: number;
    name: string;
    status: string;
    scheduled_at: string | null;
    item_count: number;
}

export interface ApiReleaseResult {
    source: ApiLivestream;
    target: ApiLivestream;
    released: Array<{
        product_master_id: number;
        sku: string | null;
        moved_quantity: number;
        target_before: number;
        target_after: number;
    }>;
    target_reverted: boolean;
}

// Unconfirmed 直播 that can receive released 未售 items (same location + warehouse).
export const getLivestreamReleaseCandidates = (id: number) =>
    request<{ candidates: ApiReleaseCandidate[] }>(`/livestreams/${id}/release-candidates`);

// Move this live's 未售 leftover (reserved − sold) into a chosen unconfirmed live.
// The source live is left unchanged; only the target gains quantity.
export const releaseLivestreamItems = (id: number, targetLivestreamId: number) =>
    request<ApiReleaseResult>(`/livestreams/${id}/release-items`, {
        method: 'POST',
        body: JSON.stringify({ target_livestream_id: targetLivestreamId }),
    });

export interface ApiBulkRemoveLivestreamItemsResult {
    livestream: ApiLivestream;
    removed_count: number;
    removed_items?: ApiLivestreamItem[];
}

export const bulkRemoveLivestreamItems = (liveId: number, itemIds?: number[]) =>
    request<ApiBulkRemoveLivestreamItemsResult>(
        `/livestreams/${liveId}/items/bulk-remove`,
        { method: 'POST', body: JSON.stringify(itemIds ? { item_ids: itemIds } : {}) },
    );

// Copy/move items to another 待开播 + 未推送 live.
export interface ApiLivestreamTransferTarget {
    id: number;
    name: string;
    live_code: string;
    live_type: string;
    item_count: number;
    scheduled_at: string | null;
}

export const listLivestreamTransferTargets = (liveId: number) =>
    request<{ items: ApiLivestreamTransferTarget[]; source_eligible: boolean }>(
        `/livestreams/${liveId}/transfer-targets`,
    );

export interface ApiLivestreamTransferResult {
    mode: 'copy' | 'move';
    copied_count: number;
    transferred_item_ids?: number[];
    created_item_ids?: number[];
    skipped_item_ids?: number[];
    removed_item_ids?: number[];
    skipped_count: number;
    removed_count: number;
    target: { id: number; name: string };
    livestream: ApiLivestream;
}

export const transferLivestreamItems = (
    liveId: number,
    payload: { target_livestream_id: number; item_ids: number[]; mode: 'copy' | 'move' },
) =>
    request<ApiLivestreamTransferResult>(`/livestreams/${liveId}/transfer-items`, {
        method: 'POST', body: JSON.stringify(payload),
    });

export const createLivestreamGroup = (liveId: number, name: string, description = '') =>
    request<ApiLivestreamGroup>(`/livestreams/${liveId}/groups`, {
        method: 'POST', body: JSON.stringify({ name, description }),
    });

export const updateLivestreamGroup = (liveId: number, groupId: number, name: string, description = '') =>
    request<ApiLivestreamGroup>(`/livestreams/${liveId}/groups/${groupId}`, {
        method: 'PATCH', body: JSON.stringify({ name, description }),
    });

export const deleteLivestreamGroup = (liveId: number, groupId: number) =>
    request<{ deleted: boolean; group_id: number }>(`/livestreams/${liveId}/groups/${groupId}`, {
        method: 'DELETE',
    });

export const reorderLivestreamGroups = (liveId: number, groupIds: number[]) =>
    request<{ groups: ApiLivestreamGroup[]; item_order: ApiLivestreamReorderResult["item_order"]; item_sort_mode: LivestreamItemSortMode }>(`/livestreams/${liveId}/groups/reorder`, {
        method: 'PATCH', body: JSON.stringify({ group_ids: groupIds }),
    });

export const assignLivestreamGroupItems = (
    liveId: number,
    itemIds: number[],
    groupId: number | null,
    targetIndex?: number,
) => request<ApiLivestream>(`/livestreams/${liveId}/groups/assign`, {
    method: 'PATCH',
    body: JSON.stringify({
        item_ids: itemIds,
        group_id: groupId,
        ...(targetIndex == null ? {} : { target_index: targetIndex }),
    }),
});

export const deleteLivestream = (id: number) =>
    request<{ message: string }>(`/livestreams/${id}`, { method: 'DELETE' });

export async function previewLivestreamOrderImport(
    liveId: number,
    file: File,
    sheetName?: string,
    sessionId?: number
): Promise<ApiLivestreamOrderImportPreview> {
    const form = new FormData();
    form.append('file', file);
    if (sheetName) form.append('sheet_name', sheetName);
    if (sessionId != null) form.append('session_id', String(sessionId));
    const res = await erpFetch(`${BASE}/livestreams/${liveId}/order-imports/preview`, {
        method: 'POST',
        body: form,
    });
    if (!res.ok) throw await apiError(res);
    return res.json();
}

export async function downloadLivestreamSessionReturn(liveId: number, sessionId: number) {
    const res = await erpFetch(`${BASE}/livestreams/${liveId}/sessions/${sessionId}/orders-file`);
    if (!res.ok) throw await apiError(res);
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
    const filename = encoded ? decodeURIComponent(encoded) : plain ?? `session-${sessionId}-orders.xlsx`;
    const url = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    return res.headers.get('X-Order-File-Origin');
}

// New uploads target one session; the array remains compatible with historical batches.
// Shipping seals the return. Sold totals remain aggregated at the live item level.
export const commitLivestreamOrderImport = (liveId: number, importId: number, sessionIds?: number[]) =>
    request<ApiLivestreamOrderImportCommitResult>(`/livestreams/${liveId}/order-imports/commit`, {
        method: 'POST',
        body: JSON.stringify({ import_id: importId, session_ids: sessionIds }),
    });

// 发货 (general): 出库 for a SET of 回传'd 场次 (总/锁定 −= 已售未发货); marks them 已发货.
export const markLivestreamSessionsShipped = (liveId: number, sessionIds: number[]) =>
    request<ApiLivestream>(`/livestreams/${liveId}/sessions/ship`, {
        method: 'POST',
        body: JSON.stringify({ session_ids: sessionIds }),
    });

// [legacy] 发货完成 for one 场次 — superseded by markLivestreamSessionsShipped.
export const markLivestreamSessionShipped = (liveId: number, sessionId: number) =>
    request<ApiLivestream>(`/livestreams/${liveId}/sessions/${sessionId}/shipped`, {
        method: 'POST',
    });

export async function previewLivestreamPlanImport(
    liveId: number,
    file: File
): Promise<ApiLivestreamPlanImportPreview> {
    const form = new FormData();
    form.append('file', file);
    return erpFormRequest<ApiLivestreamPlanImportPreview>(`/livestreams/${liveId}/plan-imports/preview`, form);
}

export async function commitLivestreamPlanImport(
    liveId: number,
    file: File
): Promise<ApiLivestreamPlanImportCommitResult> {
    const form = new FormData();
    form.append('file', file);
    return erpFormRequest<ApiLivestreamPlanImportCommitResult>(`/livestreams/${liveId}/plan-imports/commit`, form);
}

export interface ApiSelectableItem {
    id: number;
    product_master_id: number;
    sku: string;
    product_name: string;
    available_quantity: number;
    incoming_quantity: number;
    on_hand_quantity: number;
    recommended_auction_price: number | null;
    recommended_buy_now_price: number | null;
    recommended_price_currency: string | null;
    suggested_price?: number | null;
    base_cost: number | null;
    base_cost_currency: string | null;
    product_master?: ApiProductSummary;
    // Confirmed pending-purchase quantity here; broader planning total in preplan.
    planned_quantity?: number;
    readiness?: ItemReadiness;
    procurement_sources?: ApiProcurementSource[];
}

// How backed a product/claim is, in ascending order of readiness.
export type ItemReadiness = 'planned' | 'purchased' | 'received';

export interface ApiSelectableList {
    items: ApiSelectableItem[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export const getLivestreamSelectableInventory = (liveId: number, params?: { q?: string; page?: number; per_page?: number }, options?: { signal?: AbortSignal }) => {
    const q = new URLSearchParams();
    if (params?.q) q.set('q', params.q);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return request<ApiSelectableList>(`/livestreams/${liveId}/selectable-inventory${qs ? `?${qs}` : ''}`, options);
};

// 货盘选品: supplier-pool (SupplierProduct) items, not-yet-digitized (no SKU/stock).
// Selecting one digitizes it into a real SKU + adds to the live as a to-purchase item.
export interface ApiPoolSelectableItem {
    source: 'pool';
    supplier_product_id: number;
    product_master_id: null;
    name: string;
    sku: string;
    supplier_id: number;
    supplier_name: string | null;
    price: number | null;
    price_currency: string | null;
    height: number | null;
    width: number | null;
    depth: number | null;
    weight: number | null;
    photo_url: string | null;
    product_labels: string[];
    available_quantity: number;
    incoming_quantity: number;
}
export interface ApiPoolSelectableList {
    items: ApiPoolSelectableItem[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}
export const getLivestreamSelectablePool = (liveId: number, params?: { q?: string; page?: number; per_page?: number }, options?: { signal?: AbortSignal }) => {
    const q = new URLSearchParams();
    if (params?.q) q.set('q', params.q);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return request<ApiPoolSelectableList>(`/livestreams/${liveId}/selectable-pool${qs ? `?${qs}` : ''}`, options);
};
export const addLivestreamPoolItem = (liveId: number, supplierProductId: number, quantity: number) =>
    request<ApiLivestreamItem>(`/livestreams/${liveId}/items/from-pool`, {
        method: 'POST',
        body: JSON.stringify({ supplier_product_id: supplierProductId, quantity }),
    });

// 直播预排 pool: any product in a 采购单 for the live's location (incl. 待采购),
// plus anything already stocked/incoming. Each item carries `readiness`.
export const getLivestreamPreplanSelectable = (liveId: number, params?: { q?: string; page?: number; per_page?: number }) => {
    const q = new URLSearchParams();
    if (params?.q) q.set('q', params.q);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return request<ApiSelectableList>(`/livestreams/${liveId}/preplan-selectable${qs ? `?${qs}` : ''}`);
};

export interface ApiLivestreamSimilarSku extends ApiImageMatchCandidate {
    product_name: string;
    product_master: ApiProductSummary;
    available_quantity: number;
    on_hand_quantity: number;
    incoming_quantity: number;
    planned_quantity: number;
    readiness: ItemReadiness;
    addable: boolean;
}

export interface ApiLivestreamSimilarSkuResponse {
    seed: {
        product_master_id: number;
        sku: string;
        product_name: string;
        product_master: ApiProductSummary;
    };
    items: ApiLivestreamSimilarSku[];
    limit: number;
}

export interface ApiLivestreamSimilarSkuAddResult {
    product_master_id: number;
    sku: string | null;
    item_id?: number;
    status: 'added' | 'skipped' | 'failed';
    message: string;
}

export const getLivestreamSimilarSkus = (liveId: number, productMasterId: number) =>
    request<ApiLivestreamSimilarSkuResponse>(
        `/livestreams/${liveId}/items/${productMasterId}/similar-skus?limit=8`,
    );

export const addLivestreamSimilarSkus = (
    liveId: number,
    items: Array<{ product_master_id: number; quantity: number; group_id: number | null }>,
) => request<{
    livestream: ApiLivestream;
    results: ApiLivestreamSimilarSkuAddResult[];
    added_count: number;
    skipped_count: number;
    failed_count: number;
}>(`/livestreams/${liveId}/similar-skus/add`, {
    method: 'POST',
    body: JSON.stringify({ items }),
});

export const bulkAddLivestreamItems = (liveId: number, items: { product_master_id: number; quantity: number }[]) =>
    request<{ added: number }>(`/livestreams/${liveId}/items/bulk-add`, { method: 'POST', body: JSON.stringify({ items }) });

export const bulkSetLivestreamItems = (liveId: number, items: { product_master_id: number; reserved_quantity: number }[]) =>
    request<{ livestream: ApiLivestream; items: ApiLivestreamItem[]; removed_items: ApiLivestreamItem[] }>(
        `/livestreams/${liveId}/items/bulk-set`,
        { method: 'PATCH', body: JSON.stringify({ items }) },
    );

// Live-first 添加商品: create a new-SKU product + add it to the live (multipart FormData).
export async function addNewSkuLivestreamItem(liveId: number, form: FormData): Promise<ApiLivestreamItem> {
    const res = await erpFetch(`${BASE}/livestreams/${liveId}/items/new-sku`, {
        method: 'POST',
        body: form,
    });
    if (res.ok) return res.json();
    throw await apiError(res);
}

// 添加未数字化商品: create a new-SKU product with on-hand stock (already bought) and add
// it to the live as inventory-backed — no 采购单 demand. Multipart form (same shape as
// addNewSkuLivestreamItem; quantity is the in-stock quantity).
export async function addUndigitizedLivestreamItem(liveId: number, form: FormData): Promise<ApiLivestreamItem> {
    const res = await erpFetch(`${BASE}/livestreams/${liveId}/items/undigitized`, {
        method: 'POST',
        body: form,
    });
    if (res.ok) return res.json();
    throw await apiError(res);
}

// Reorder returns a lightweight confirmation (not the full livestream) — the
// client already holds the new order optimistically.
export interface ApiLivestreamReorderResult {
    id: number;
    status: LivestreamStatus;
    item_sort_mode: LivestreamItemSortMode;
    item_order: { id: number; sort_order: number; custom_sort_order: number }[];
}

export type LivestreamItemSortMode =
    | 'custom'
    | 'diameter_asc'
    | 'diameter_desc'
    | 'price_asc'
    | 'price_desc';

export const reorderLivestreamItems = (
    liveId: number,
    itemIds?: number[],
    sortMode: LivestreamItemSortMode = 'custom',
) =>
    request<ApiLivestreamReorderResult>(`/livestreams/${liveId}/items/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ ...(itemIds ? { item_ids: itemIds } : {}), sort_mode: sortMode }),
    });

export interface ApiLivestreamUnit { item_id: number; page_break: boolean }

export interface PrintDispatchResult {
    success: boolean;
    sent: number;
    failed: number;
    total: number;
    summary_email_task?: LivestreamSummaryEmailTaskResult;
    summary_email_error?: string;
}

// One label's data — the frontend renders each to an image, then dispatches.
interface ApiPrintLabelBase {
    sku: string;
    price: number | null;
    livestream_name: string;
    number: number | null; // 序号 in the live; null = 无关联场次 (blank)
    dimensions?: string; // 外部 长宽高 "L## W## D##" (inch)
}

export interface ApiLivestreamPrintLabel extends ApiPrintLabelBase {
    group_name?: string; // 该 SKU 在当前直播中的分组名称
}

export interface ApiProductPrintLabel extends ApiPrintLabelBase {
    item_label?: string; // 首个采购标签 (product_labels[0])
}

// Label data for a livestream's units (直播 打印标签). `number` scopes to one
// SKU position and can return multiple physical-unit labels for that SKU.
export const getLivestreamPrintLabels = (liveId: number, number?: number) => {
    const q = number != null ? `?number=${number}` : '';
    return request<{ labels: ApiLivestreamPrintLabel[] }>(`/livestreams/${liveId}/print-labels${q}`);
};

// Label data for a product's livestream units (商品库 / 采购单 打印标签).
// 传 lineId 时按该采购行的已接单件数出标签：直播不再需要的那几件带空白场次信息，
// 件数在 orphan_count 里，收货时要进备用库位。
export const getProductPrintLabels = (productMasterId: number, orderId?: number, lineId?: number) => {
    const q = new URLSearchParams({ product_master_id: String(productMasterId) });
    if (orderId != null) q.set('order_id', String(orderId));
    if (lineId != null) q.set('line_id', String(lineId));
    return request<{ labels: ApiProductPrintLabel[]; orphan_count?: number }>(`/product-print-labels?${q.toString()}`);
};

// Dispatch fully-rendered label images to 快麦云 raster print (imageWrite).
export const printLabelImages = (
    images: { image: string; sku: string }[],
    printTimes = 1,
    livestreamId?: number,
    triggerSummaryEmail = false,
) =>
    request<PrintDispatchResult>(`/print-label-images`, {
        method: 'POST',
        body: JSON.stringify({
            images,
            print_times: printTimes,
            livestream_id: livestreamId,
            trigger_summary_email: triggerSummaryEmail,
        }),
    });

/** @deprecated New clients use SKU-level sort_order/reorderLivestreamItems. */
// Retained for compatibility with older clients that persist per-unit positions.
export const setLivestreamUnitOrder = (liveId: number, unitOrder: ApiLivestreamUnit[]) =>
    request<{ id: number; status: string; unit_order: ApiLivestreamUnit[] }>(
        `/livestreams/${liveId}/unit-order`,
        { method: 'PATCH', body: JSON.stringify({ unit_order: unitOrder }) },
    );

export const updateLivestreamItem = (
    liveId: number,
    itemId: number,
    payload: Partial<{
        reserved_quantity: number;
        sold_quantity: number;
        sort_order: number;
        selection_metadata: ApiLivestreamSelectionMetadata;
        selection_story: ApiLivestreamStoryMetadata;
        final_listing_price: number | null;
        final_listing_price_currency: string | null;
    }>
) => request<ApiLivestreamItem>(`/livestreams/${liveId}/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(payload) });

export const deleteLivestreamItem = (liveId: number, itemId: number) =>
    request<{ deleted: boolean; item_id: number }>(`/livestreams/${liveId}/items/${itemId}`, { method: 'DELETE' });

// ─── Procurement ──────────────────────────────────────────────────────────────

export type ProcurementStatus = 'draft' | 'in_progress' | 'completed' | 'receiving_exception';
export type LineStatus = 'unsubmitted' | 'pending_purchase' | 'purchased' | 'receiving' | 'completed' | 'exception' | 'out_of_stock';

export interface ApiProcurementOrder {
    id: number;
    order_code: string;
    name: string;
    description: string;
    status: ProcurementStatus;
    source_type?: 'manual' | 'product_import' | string;
    status_label: string;
    suppliers: Array<{ id: number; supplier_id: number; name: string; supplier_name?: string | null; brand_story?: string | null; supplier_brand_story?: string | null }>;
    target_quantity: number;
    target_warehouse_id: number | null;
    target_warehouse_code: string | null;
    target_warehouse_name: string | null;
    // The order's stock lands in this warehouse's inventory location. A livestream
    // can only pull from orders whose location matches its own.
    target_warehouse?: {
        id: number;
        name: string;
        warehouse_type: 'physical' | 'non_stock';
        inventory_location_id: number | null;
        inventory_location_code: string | null;
        inventory_location_name: string | null;
    } | null;
    line_count: number;
    total_purchased_quantity: number;
    total_received_quantity: number;
    estimated_arrival_date: string | null;
    completed_at: string | null;
    added_to_livestream_id: number | null;
    added_to_livestream_name: string | null;
    livestream_additions?: Array<{
        livestream_id: number;
        livestream_name: string | null;
        livestream_code: string | null;
        quantity_added: number;
    }>;
    livestream_imports?: Array<{
        livestream_id: number;
        livestream_name: string | null;
        livestream_code: string | null;
        processed_sku_count: number;
    }>;
    size_bucket_thresholds: number[] | null;
    size_bucket_targets: number[] | null;
    created_at: string;
    updated_at: string;
    tracking_numbers: Array<{ no: string; company: string }>;
}

export type AiPlanningReadiness = 'low' | 'medium' | 'high';

export interface ApiAiPlanningProject {
    id: number;
    name: string;
    status: string;
    location_id: number;
    location: ApiInventoryLocationInfo | null;
    procurement_order_ids: number[];
    procurement_orders?: Array<{ id: number; name: string | null; order_code: string }>;
    latest_enrichment_job?: ApiProductEnrichmentJob | null;
    created_at: string;
    updated_at: string;
}

export interface ApiAiPlanningCandidate {
    product_master_id: number;
    sku: string;
    readiness: AiPlanningReadiness;
    missing_fields: string[];
    label_enrichment_eligible: boolean;
    attribute_enrichment_eligible: boolean;
    attribute_status: 'complete' | 'partial' | 'invalid';
    cost_hint: { amount: number; currency: string; source: string } | null;
    product_master: ApiProductSummary;
    source_order_ids: number[];
}

export interface ApiAiPlanningReadinessResult {
    project: ApiAiPlanningProject;
    summary: Record<string, number>;
    items: ApiAiPlanningCandidate[];
    pagination: { total: number; page: number; per_page: number; pages: number };
}

export interface ApiProductEnrichmentItem {
    id: number;
    job_id: number;
    product_master_id: number;
    sku: string | null;
    status: string;
    attempt_count: number;
    generated_labels: string[];
    labels_written: string[];
    attributes_before: ApiProductSummary['attribute_values'];
    generated_attributes: Record<string, unknown>;
    attributes_written: Record<string, string[]>;
    attribute_suggestions: Record<string, string[]>;
    attribute_evidence: Record<string, string>;
    confidence: string | null;
    needs_review: boolean;
    unique_shape_score?: number;
    unique_shape_reasons?: string[];
    short_reason: string | null;
    review_reason: string | null;
    error_message: string | null;
    usage?: Record<string, unknown> | null;
}

export interface ApiProductEnrichmentJob {
    id: number;
    project_id: number;
    status: string;
    write_mode: 'replace_empty';
    targets: Array<'labels' | 'attributes' | 'merchandising' | 'pot_visual_language'>;
    model: string;
    prompt_version: string;
    requested_count: number;
    counts: Record<string, number>;
    items?: ApiProductEnrichmentItem[];
    error_message: string | null;
    created_at: string;
    updated_at: string;
}

export interface ApiApplyProductEnrichmentAttributesResult {
    item: ApiProductEnrichmentItem;
    attribute_values: ApiProductSummary['attribute_values'];
}

export const getAiPlanningSources = (locationId: number) =>
    request<{ items: ApiProcurementOrder[] }>(`/ai-planning/sources?location_id=${locationId}`);

export const getAiPlanningProjects = (page = 1, perPage = 20) =>
    request<{ items: ApiAiPlanningProject[]; total: number; page: number; per_page: number; pages: number }>(`/ai-planning/projects?page=${page}&per_page=${perPage}`);

export const createAiPlanningProject = (payload: { name?: string; location_id: number; procurement_order_ids: number[] }) =>
    request<ApiAiPlanningProject>('/ai-planning/projects', { method: 'POST', body: JSON.stringify(payload) });

export const getAiPlanningProject = (projectId: number) =>
    request<ApiAiPlanningProject>(`/ai-planning/projects/${projectId}`);

export const updateAiPlanningProject = (projectId: number, payload: { name: string }) =>
    request<ApiAiPlanningProject>(`/ai-planning/projects/${projectId}`, { method: 'PATCH', body: JSON.stringify(payload) });

export const addAiPlanningProjectSources = (projectId: number, procurementOrderIds: number[]) =>
    request<ApiAiPlanningProject>(`/ai-planning/projects/${projectId}/procurement-orders`, {
        method: 'POST', body: JSON.stringify({ procurement_order_ids: procurementOrderIds }),
    });

export const getAiPlanningReadiness = (projectId: number, page = 1, perPage = 60, readiness = '', includeSummary = true) =>
    request<ApiAiPlanningReadinessResult>(`/ai-planning/projects/${projectId}/readiness?page=${page}&per_page=${perPage}&include_summary=${includeSummary ? '1' : '0'}${readiness ? `&readiness=${readiness}` : ''}`);

export const createProductEnrichmentJob = (projectId: number, productMasterIds?: number[], targets: Array<'labels' | 'attributes'> = ['labels', 'attributes']) =>
    request<ApiProductEnrichmentJob>(`/ai-planning/projects/${projectId}/product-enrichment-jobs`, {
        method: 'POST', body: JSON.stringify({ product_master_ids: productMasterIds ?? [], targets }),
    });

export const getProductEnrichmentJob = (jobId: number) =>
    request<ApiProductEnrichmentJob>(`/ai-planning/product-enrichment-jobs/${jobId}`);

export const retryProductEnrichmentJob = (jobId: number, itemIds?: number[]) =>
    request<ApiProductEnrichmentJob>(`/ai-planning/product-enrichment-jobs/${jobId}/retry`, {
        method: 'POST', body: JSON.stringify({ item_ids: itemIds ?? [] }),
    });

export const applyProductEnrichmentAttributes = (itemId: number) =>
    request<ApiApplyProductEnrichmentAttributesResult>(`/ai-planning/product-enrichment-items/${itemId}/apply-attributes`, {
        method: 'POST',
    });

export interface ApiMerchandisingPlanItem {
    sku: string;
    run_order: number;
    group: string;
    price_tier: '+' | '++' | '+++' | null;
    selection_target_tier: '+' | '++' | '+++' | 'average' | null;
    selection_source: 'quota' | 'backfill';
    backfill_for: string | null;
    selling_points: string[];
    reason: string;
    primary_decoration: string;
    needs_review: boolean;
    visual_summary: {
        color: string | null;
        front_profile: string | null;
        opening_shape: string | null;
        subjects: string[];
        pattern: string | null;
    };
}

export interface ApiMerchandisingGroupDiagnostic {
    group: string;
    quota: number;
    raw_candidate_count: number;
    white_filtered_count: number;
    overpriced_filtered_count: number;
    eligible_candidate_count: number;
    candidate_count: number;
    target_tier_counts: Record<string, number>;
    selected_tier_counts: Record<string, number>;
    fallback_count: number;
    fallbacks: Array<{ target_tier: string; actual_tier: string; sku: string }>;
}

export interface ApiMerchandisingPlan {
    items: ApiMerchandisingPlanItem[];
    import_rows: Array<Record<string, string | number | boolean>>;
    selected_count: number;
    quota_selected_count: number;
    backfilled_count: number;
    target_count: number;
    quota_gaps: Array<{ group: string; missing: number }>;
    gaps: Array<{ group: string; missing: number }>;
    group_diagnostics: ApiMerchandisingGroupDiagnostic[];
    manual_groups: Array<{ group: string; count: number }>;
    selection_excluded: Array<{ sku: string; group: string; reasons: string[] }>;
    selection_seed: string | null;
}

export interface ApiMerchandisingPreview {
    strategy_version: string;
    prompt_version: string;
    model: string;
    classification_pipeline: 'visual_facts_v0.4';
    target_visual_language_version?: string | null;
    plan: ApiMerchandisingPlan;
    coverage: ApiMerchandisingCounts;
    plan_ready: boolean;
    review_policy: 'warning_only';
    unique_review: ApiMerchandisingPlanItem[];
    excluded: Array<{ sku: string; group?: string; reasons: string[] }>;
    warnings: string[];
    visual_language_versions: string[];
    derivation_version?: string | null;
    projection_version: string;
    projection_hash: string | null;
    selection_profile_version: string;
    selection_profile_hash: string;
    selection_engine_version: string;
    selection_seed: string | null;
    selection_profile_snapshot: ApiSelectionProfile;
    sales_theme_profile_snapshot?: ApiSelectionProfile | null;
}

export interface ApiSelectionProfile { version: string; description: string; multiplier: number; rounding: 'ceil' | 'exact'; selection_engine_version: string; backfill: { enabled: boolean; tiers: string[]; deduplicate: boolean }; groups: Array<{ name: string; priority: number; quotas: Record<string, number> }> }
export interface ApiSelectionProfileSummary { groups?: Array<{ name: string; quotas: Record<string, number> }>; version: string; description: string; multiplier: number; rounding: 'ceil' | 'exact'; selection_engine_version: string; backfill_enabled: boolean; hash: string }
export interface ApiSavedMerchandisingPlan { plan_id: string; project_id: number; content_hash: string; selection_seed: string | null; result: ApiMerchandisingPreview }

export interface ApiLivePlanDraft {
    plan_id: string;
    project_id: number;
    project_location_id: number;
    content_hash: string;
    input_mode: 'planning_brief' | 'legacy_presenter_copy' | 'template';
    original_input: { planning_brief: string } | { description: string; product_pitch_script: string } | { selection_mode: 'template'; profile_version: string; procurement_order_ids: number[]; sales_theme_enabled: boolean; sales_theme_count: number };
    planning_input_hash: string;
    application_token: string;
    application_token_expires_in: number;
    interpreted_intent: string;
    assumptions: string[];
    model: string;
    prompt_version: string;
    result: ApiMerchandisingPreview & {
        interpreted_intent: string;
        assumptions: string[];
        plan: ApiMerchandisingPreview['plan'] & {
            groups: Array<{ name: string; description: string; target_count: number; mandatory_review: boolean }>;
        };
    };
}

export interface ApiMerchandisingCounts {
    total_eligible: number;
    classified: number;
    queued: number;
    processing: number;
    flagged: number;
    failed: number;
    unknown: number;
    remaining: number;
    coverage: number;
}

export const getSelectionProfiles = () => request<{ default_version: string; items: ApiSelectionProfileSummary[] }>('/ai-planning/selection-profiles');

export interface ApiPotKnowledgeVersion {
    version: string;
    title?: string;
    status: 'frozen' | 'invalid';
    valid: boolean;
    bundle_sha256?: string;
    knowledge_version?: string;
    prompt_version?: string;
    derivation_version?: string;
    errors: string[];
}

export interface ApiPotKnowledgeBundle {
    manifest: ApiPotKnowledgeVersion & Record<string, unknown>;
    schema: { fields: Array<{
        key: string;
        type: string;
        multi_value: boolean;
        applicability?: string;
        labels?: Record<string, string>;
        definitions?: Record<string, string>;
        track?: 'A' | 'B';
        measurement_status?: 'model_estimated' | 'unavailable' | 'not_applicable';
    }> };
    vocabulary: { values: Record<string, Array<string | { key: string; labels?: Record<string, string> }>> };
    ontology: { relationships: unknown[] };
    response_schema: Record<string, unknown>;
    prompt: string;
    derivation_rules: Record<string, unknown>;
    projection?: {
        version: string;
        derivation_version: string;
        rules: Array<{ id: string; output_flag: string; condition: Record<string, unknown>; evidence_dependencies: string[]; unknown_behavior: string; explanation: string }>;
    };
}

export interface ApiPotClassificationPreview {
    project_id: number;
    bundle_version: string;
    bundle_sha256: string;
    model: string;
    reasoning_effort: string;
    max_cost_usd: number;
    configured_max_cost_usd: number;
    confirmation_hash: string;
    sku_count: number;
    image_count: number;
    estimated_cost_usd: number;
    raw_po_line_count: number; unique_product_master_count: number;
    reused_classification_count: number; new_classification_count: number;
    skipped_product_count: number; conflicted_product_count: number;
    unpreviewed_product_count: number;
    identity_conflicts: Array<Record<string, unknown>>;
    skipped_products: Array<Record<string, unknown>>;
    pricing: Record<string, unknown>;
    automatic_max_cost_usd: number | null;
    jobs_count: number;
    chunk_size: number;
    chunks: Array<{ index: number; sku_count: number; estimated_cost_usd: number; max_cost_usd: number }>;
    mode: 'selected' | 'trial' | 'batch' | 'all' | 'upgrade' | 'retry_failed' | 'rerun';
    eligible_count?: number;
    active_skipped_count?: number;
    remaining_total: number | null;
    remaining_after_batch: number | null;
    items?: Array<{ product_master_id: number; sku: string; images: Array<{ asset_id: number | null; role: string; url: string }>; estimated_cost_usd: number }>;
    prompt: string;
    schema: ApiPotKnowledgeBundle['schema'];
    response_schema: Record<string, unknown>;
    derivation_rules: Record<string, unknown>;
}

export interface ApiPotClassificationBatchResult {
    jobs_created: number;
    job_ids: number[];
    requested_count: number;
    chunk_size: number;
    estimated_cost_usd: number;
    max_cost_usd: number;
    jobs: ApiProductEnrichmentJob[];
}

export interface ApiPotObservation {
    field: string; scope_id: string;
    state: 'value' | 'unknown' | 'not_visible' | 'not_applicable' | 'out_of_vocabulary';
    value: string | Record<string, unknown> | null;
    image_ids: string[]; evidence: string;
    confidence_band: 'high' | 'medium' | 'low';
    review_status: 'pending' | 'reviewed' | 'conflict';
    schema_version: string; candidate_description: string | null;
    unavailable_children: Array<{ path: string; reason: string }>;
}
export interface ApiPotFact {
    state: string; values: string[]; confidence: number; evidence: string;
    observations?: ApiPotObservation[];
    unavailable_reason?: string | null;
}
export interface ApiPotClassificationItem {
    id: number;
    job_id: number;
    product_master_id: number;
    sku: string;
    status: string;
    attempt_count: number;
    images: Array<{ asset_id: number | null; role: string; url: string; content_sha256?: string }>;
    schema_version?: string;
    provenance?: Record<string, unknown>;
    instances?: Array<Record<string, unknown>>;
    features?: Array<Record<string, unknown>>;
    measurements?: Array<Record<string, unknown>>;
    color_regions?: Array<Record<string, unknown>>;
    model_facts: Record<string, ApiPotFact>;
    model_derived_flags: Record<string, boolean | null>;
    effective_facts: Record<string, ApiPotFact>;
    effective_derived_flags: Record<string, boolean | null>;
    derivation_version?: string | null;
    projection_version?: string | null;
    visual_description: string;
    review_diagnostics: {
        reason_codes?: string[]; conflicting_fields?: string[];
        planning_relevant_missing_fields?: string[]; evidence_insufficient_count?: number;
        oov_candidates?: Array<Record<string, unknown>>; quarantine_count?: number;
        semantic_conflicts?: string[];
        data_quality_issues?: Array<{ field?: string; category: 'scope_mismatch' | 'image_scope_mismatch' | 'invalid_payload'; scope_id?: string; image_ids?: string[] }>;
        evidence_insufficient?: Array<{ field: string; reason?: string | null }>;
        out_of_vocabulary?: Array<Record<string, unknown>>;
    };
    correction_revision: number;
    correction_events: Array<Record<string, unknown>>;
    evidence: { quarantine?: Array<Record<string, unknown>>; raw_response?: unknown };
    confidence: string | null;
    needs_review: boolean;
    review_reason: string | null;
    error_message: string | null;
    usage?: Record<string, unknown>;
}

export interface ApiPotClassificationSummary {
    id: number;
    job_id: number;
    product_master_id: number;
    sku: string;
    status: string;
    attempt_count: number;
    schema_version: string;
    confidence: string | null;
    correction_revision: number;
    effective_derived_flags: Record<string, boolean | null>;
    error_message: string | null;
    image_asset_ids: number[];
    projection_version?: string | null;
}

export const getPotKnowledgeVersions = () =>
    request<{ items: ApiPotKnowledgeVersion[]; default_version: string }>('/ai-planning/pot-visual-language/versions');

export const getPotKnowledgeVersion = (version: string) =>
    request<ApiPotKnowledgeBundle>(`/ai-planning/pot-visual-language/versions/${encodeURIComponent(version)}`);

export const getPotKnowledgeDiff = (version: string, against: string) =>
    request<Record<string, unknown>>(`/ai-planning/pot-visual-language/versions/${encodeURIComponent(version)}/diff?against=${encodeURIComponent(against)}`);

export interface ApiPotDerivationVersion {
    version: string; derivation_version: string; compatible_bundle: string; hash: string; rule_count: number;
}

export const getPotDerivationVersions = (compatibleBundle: string) =>
    request<{ default_version: string | null; items: ApiPotDerivationVersion[] }>(`/ai-planning/pot-derivation-versions?compatible_bundle=${encodeURIComponent(compatibleBundle)}`);

export const previewPotClassificationJob = (projectId: number, payload: Record<string, unknown>) =>
    request<ApiPotClassificationPreview>(`/ai-planning/projects/${projectId}/pot-classification-jobs/preview`, { method: 'POST', body: JSON.stringify(payload) });

export const createPotClassificationJob = (projectId: number, payload: Record<string, unknown>) =>
    request<ApiProductEnrichmentJob | ApiPotClassificationBatchResult>(`/ai-planning/projects/${projectId}/pot-classification-jobs`, { method: 'POST', body: JSON.stringify(payload) });

export const getPotClassificationJobs = (projectId: number) =>
    request<{ items: ApiProductEnrichmentJob[] }>(`/ai-planning/projects/${projectId}/pot-classification-jobs`);

export interface ApiPotClassificationCoverage {
    target_version: string; target_bundle_sha256: string; total_eligible: number;
    successful: number; flagged: number; queued: number; processing: number;
    failed: number; outdated: number; unclassified: number; ready_for_final_plan: boolean;
}

export interface ApiPotSubmissionProgress {
    confirmation_hash: string; version: string; submission_mode: string | null;
    total: number; completed: number; flagged: number; failed: number; queued: number; processing: number;
    percentage: number; created_at: string | null; updated_at: string | null;
    current_item: { item_id: number; job_id: number; product_master_id: number; sku: string; processing_started_at: string | null } | null;
    jobs: Array<Pick<ApiProductEnrichmentJob, 'id' | 'status' | 'requested_count' | 'counts' | 'created_at' | 'updated_at'>>;
}

export const getPotClassificationProgress = (projectId: number, signal?: AbortSignal) =>
    request<{ items: ApiPotSubmissionProgress[] }>(`/ai-planning/projects/${projectId}/pot-classification-progress`, { signal });

export const getPotClassificationCoverage = (projectId: number, version: string, signal?: AbortSignal) =>
    request<ApiPotClassificationCoverage>(`/ai-planning/projects/${projectId}/pot-classification-coverage?version=${encodeURIComponent(version)}`, { signal });

export const getPotProjectClassificationItems = (projectId: number, params: { version: string; page?: number; per_page?: number; status?: string; sku?: string; projection_version?: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => value !== undefined && value !== '' && query.set(key, String(value)));
    return request<{ items: ApiPotClassificationSummary[]; pagination: { total: number; page: number; per_page: number; pages: number } }>(`/ai-planning/projects/${projectId}/pot-classification-results?${query}`, { signal });
};

export const getPotProjectClassificationItemDetail = (projectId: number, itemId: number, params: { version: string; projection_version?: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => value !== undefined && value !== '' && query.set(key, String(value)));
    return request<ApiPotClassificationItem>(`/ai-planning/projects/${projectId}/pot-classification-results/${itemId}?${query}`, { signal });
};

export interface ApiPotDiscoveryReportSummary {
    submission_hash: string; submission_mode: string; bundle_version: string; created_at: string;
    candidate_group_count: number; affected_sku_count: number; job_ids: number[];
    synthesis: { status: string; usage?: { cost_usd?: number }; estimated_cost_usd?: number; error?: string };
}
export interface ApiPotDiscoveryReport extends ApiPotDiscoveryReportSummary {
    candidate_snapshot: Array<Record<string, unknown>>;
    synthesis_input?: Record<string, unknown>;
    synthesis: ApiPotDiscoveryReportSummary['synthesis'] & { recommendations?: Array<{
        action: string; field: string; candidate: string; proposed_value: string | null;
        reason: string; confidence: number; supporting_skus: string[]; supporting_sku_count: number;
    }> };
}
export const getPotDiscoveryStages = (projectId: number, signal?: AbortSignal) =>
    request<{ items: ApiPotDiscoveryReportSummary[]; available: boolean; reason?: string }>(`/ai-planning/projects/${projectId}/pot-discovery-stages`, { signal });
export const getPotDiscoveryStage = (projectId: number, submissionHash: string) =>
    request<ApiPotDiscoveryReport>(`/ai-planning/projects/${projectId}/pot-discovery-stages/${submissionHash}`);
export const retryPotDiscoverySynthesis = (projectId: number, submissionHash: string) =>
    request<ApiPotDiscoveryReport>(`/ai-planning/projects/${projectId}/pot-discovery-stages/${submissionHash}`, { method: 'POST' });
export const potDiscoveryStageDownloadUrl = (projectId: number, submissionHash: string) =>
    `${BASE}/ai-planning/projects/${projectId}/pot-discovery-stages/${submissionHash}?download=true`;

export const getPotClassificationItems = (jobId: number, params: { page?: number; per_page?: number; status?: string; sku?: string; review_required?: boolean; projection_version?: string } = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => value !== undefined && value !== '' && query.set(key, String(value)));
    return request<{ items: ApiPotClassificationItem[]; pagination: { total: number; page: number; per_page: number; pages: number } }>(`/ai-planning/pot-classification-jobs/${jobId}/items?${query}`);
};

export const correctPotClassificationItem = (itemId: number, payload: Record<string, unknown>) =>
    request<ApiPotClassificationItem>(`/ai-planning/pot-classification-items/${itemId}/corrections`, { method: 'POST', body: JSON.stringify(payload) });

export const potClassificationExportUrl = (jobId: number, format: 'json' | 'csv') =>
    `${BASE}/ai-planning/pot-classification-jobs/${jobId}/export?format=${format}`;

export interface ApiProcurementList {
    items: ApiProcurementOrder[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export const getProcurements = (params?: {
    status?: string;
    statuses?: ProcurementStatus[];
    warehouse_id?: number;
    q?: string;
    page?: number;
    per_page?: number;
    sort?: string;
}, opts?: { signal?: AbortSignal }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    params?.statuses?.forEach(status => q.append('statuses', status));
    if (params?.warehouse_id) q.set('warehouse_id', String(params.warehouse_id));
    if (params?.q) q.set('q', params.q);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    if (params?.sort) q.set('sort', params.sort);
    const qs = q.toString();
    return request<ApiProcurementList>(`/procurements${qs ? `?${qs}` : ''}`, { signal: opts?.signal });
};

export const createProcurement = (payload: {
    name: string;
    description?: string;
    target_quantity?: number;
    target_warehouse_id?: number;
    estimated_arrival_date?: string;
    supplier_ids?: number[];
    size_bucket_thresholds?: number[];
    size_bucket_targets?: number[];
}) => request<ApiProcurementOrder>('/procurements', { method: 'POST', body: JSON.stringify(payload) });

export const deleteProcurement = (id: number) =>
    request<{ message: string }>(`/procurements/${id}`, { method: 'DELETE' });

export const updateProcurement = (id: number, payload: Partial<{
    name: string;
    description: string;
    target_quantity: number;
    target_warehouse_id: number;
    estimated_arrival_date: string;
    tracking_numbers: { no: string; company: string }[];
    supplier_ids: number[];
}>) => request<ApiProcurementOrder>(`/procurements/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });

export const generatePurchaseOrder = (id: number, payload: {
    target_warehouse_id: number;
    name?: string;
    description?: string;
    estimated_arrival_date?: string;
    line_quantities?: Record<string, number>;
    generation_idempotency_key?: string;
}) => request<ApiProcurementDetail>(`/procurements/${id}/generate-purchase-order`, {
    method: 'POST', body: JSON.stringify(payload),
});

export interface ApiProcurementLine {
    id: number;
    order_id: number;
    line_type: string;
    product_master_id: number | null;
    sku: string | null;
    target_quantity: number;
    // Minimum 目标数 demanded by confirmed-预排 livestreams (increase-only floor).
    livestream_demand_quantity?: number;
    preplan_sequence_number?: number | null;
    preplan_sequence_numbers?: Array<{
        livestream_id: number;
        livestream_name: string | null;
        livestream_code: string | null;
        sequence_number: number;
        sort_order: number;
    }>;
    min_purchase_quantity: number | null;
    max_purchase_quantity: number | null;
    purchased_quantity: number;
    received_quantity: number;
    purchase_price: number | null;
    purchase_currency: string | null;
    sea_recommended_auction_price?: number | null;
    air_recommended_auction_price?: number | null;
    recommended_price_currency?: string | null;
    status: LineStatus;
    procurement_status: LineStatus;
    status_label: string;
    supplier_id: number | null;
    supplier_name: string | null;
    supplier_sku: string | null;
    // Free-form line note. Used for the 异常说明 (exception explanation).
    notes: string | null;
    original_url: string | null;
    latest_poster_status: CoverPosterJobStatus | null;
    product_master?: ApiProductSummary;
    created_at: string;
    updated_at: string;
}

export interface ApiProductMasterUpdate {
    success: boolean;
    data: {
        master_id: number;
        name: string | null;
        display_name: string;
        height: number | null;
        width: number | null;
        depth: number | null;
        internal_height: number | null;
        internal_width: number | null;
        internal_depth: number | null;
        weight: number | null;
        opening_diameter: number | null;
        unit: string | null;
        category_id: number | null;
        purchase_url: string | null;
        product_labels: string[];
        updated_fields: string[];
    };
}

export interface ApiProductMasterPhotoReplace {
    success: boolean;
    data: {
        id: number;
        sku: string;
        source_original_url: string | null;
        original_url: string | null;
        original_image_status: string | null;
        source_image_kind: ApiSourceImageKind | null;
    };
}
export interface ApiProcurementDetail extends ApiProcurementOrder {
    lines: ApiProcurementLine[];
    line_pagination?: { total: number; page: number; per_page: number; pages: number };
}

interface ProcurementReadParams {
    page?: number;
    perPage?: number;
    q?: string;
    status?: string;
    supplierId?: number;
    allLines?: boolean;
    sort?: string;
}

const procurementReadPath = (id: number, params?: ProcurementReadParams) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));
    if (params?.q) query.set('q', params.q);
    if (params?.status) query.set('status', params.status);
    if (params?.supplierId) query.set('supplier_id', String(params.supplierId));
    if (params?.allLines) query.set('all_lines', '1');
    if (params?.sort) query.set('sort', params.sort);
    const qs = query.toString();
    return `/procurements/${id}${qs ? `?${qs}` : ''}`;
};

export const getProcurement = (id: number, params?: ProcurementReadParams, options?: { signal?: AbortSignal }) =>
    request<ApiProcurementDetail>(procurementReadPath(id, params), options);

/** Display-only seed. Every mounted detail still revalidates with the server. */
export const getCachedProcurement = (id: number, params?: ProcurementReadParams) =>
    peekQuery<ApiProcurementDetail>(`raw|${BASE}${procurementReadPath(id, params)}`, 30_000);

// 确认采购单：草稿 → 进行中（仅手动采购单需要；直播生成的采购单创建即为进行中）。
export const confirmProcurement = (id: number) =>
    request<ApiProcurementDetail>(`/procurements/${id}/confirm`, { method: 'POST' });


export interface ApiBatchImportResult {
    created_count: number;
    failed_count: number;
    created: Array<{ excel_row: number; sku: string; product_name: string }>;
    failed: Array<{ excel_row: number; reason: string }>;
    order: ApiProcurementDetail;
}

// Batch-add new-SKU lines to a draft 采购单 from an uploaded xlsx template.
// Streams newline-delimited JSON progress; onProgress fires as rows are processed.
export async function batchImportProcurementLines(
    orderId: number,
    file: File,
    onProgress?: (done: number, total: number) => void,
): Promise<ApiBatchImportResult> {
    const form = new FormData();
    form.append('file', file);
    const res = await erpFetch(`${BASE}/procurements/${orderId}/lines/batch-import`, {
        method: 'POST',
        body: form,
    });
    if (!res.ok || !res.body) {
        const body = await responseErrorBody(res);
        throw new ApiRequestError(responseErrorMessage(res, body), res.status, body);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: ApiBatchImportResult | null = null;

    const handleLine = (line: string) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        const evt = JSON.parse(trimmed) as
            | { type: 'start'; total: number }
            | { type: 'progress'; done: number; total: number }
            | { type: 'done'; result: ApiBatchImportResult };
        if (evt.type === 'start') onProgress?.(0, evt.total);
        else if (evt.type === 'progress') onProgress?.(evt.done, evt.total);
        else if (evt.type === 'done') result = evt.result;
    };

    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf('\n')) >= 0) {
            handleLine(buffer.slice(0, idx));
            buffer = buffer.slice(idx + 1);
        }
    }
    if (buffer.trim()) handleLine(buffer);
    if (!result) throw new Error('导入未完成，请重试');
    return result;
}

export const addProcurementLine = (orderId: number, payload: {
    line_type: string;
    target_quantity?: number;
    supplier_id?: number;
    supplier_sku?: string;
    notes?: string;
    original_url?: string;
    purchase_url?: string;
    purchase_price?: number;
    purchase_currency?: string;
    height?: number;
    width?: number;
    depth?: number;
    internal_height?: number;
    internal_width?: number;
    internal_depth?: number;
    weight?: number;
    unit?: string;
    category_id?: number;
    product_labels?: string[];
    attribute_ids?: number[];
    attribute_option_ids?: number[];
}) => request<ApiProcurementLine>(`/procurements/${orderId}/lines`, { method: 'POST', body: JSON.stringify(payload) });

export async function createNewSkuProcurementLine(orderId: number, photo: File, payload: {
    bottom_photo?: File;
    bottom_not_applicable?: boolean;
    other_photos?: File[];
    target_quantity?: number;
    supplier_id?: number;
    supplier_sku?: string;
    notes?: string;
    purchase_price?: number;
    purchase_currency?: string;
    purchase_url?: string;
    height?: number;
    width?: number;
    depth?: number;
    internal_height?: number;
    internal_width?: number;
    internal_depth?: number;
    weight?: number;
    unit?: string;
    category_id?: number;
    product_labels?: string[];
    attribute_ids?: number[];
    attribute_option_ids?: number[];
    min_purchase_quantity?: number;
    max_purchase_quantity?: number;
}): Promise<ApiProcurementLine> {
    const form = new FormData();
    form.append('photo', photo);
    if (payload.bottom_photo) form.append('bottom_photo', payload.bottom_photo);
    if (payload.bottom_not_applicable !== undefined) {
        form.append('bottom_not_applicable', String(payload.bottom_not_applicable));
    }
    payload.other_photos?.forEach(other => form.append('other_photos', other));
    Object.entries(payload).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
            form.append(k, Array.isArray(v) ? JSON.stringify(v) : String(v));
        }
    });

    const attempts = 3;
    const delays = [300, 900];
    let lastError: Error | null = null;

    for (let i = 0; i < attempts; i++) {
        try {
            const res = await erpFetch(`${BASE}/procurements/${orderId}/lines/new-sku`, {
                method: 'POST',
                body: form,
            });
            if (res.ok) return res.json();
            const error = await apiError(res);
            // Only retry on 5xx; 4xx is a validation error that won't change on retry.
            if (res.status >= 500 && i < attempts - 1) {
                lastError = error;
            } else {
                throw error;
            }
        } catch (err) {
            lastError = err instanceof Error ? err : new Error(String(err));
            if (i === attempts - 1) throw lastError;
        }
        await new Promise(r => setTimeout(r, delays[i]));
    }
    throw lastError ?? new Error('添加失败，请重试');
}

export interface ApiImageMatchCandidate {
    rank: number;
    embedding_id?: number;
    product_master_id: number;
    sku: string;
    display_name?: string | null;
    product_name?: string | null;
    image_url?: string | null;
    product_original_url?: string | null;
    distance: number | null;
    match_bucket: 'strong' | 'possible' | 'weak' | string;
    inventory_summary?: {
        on_hand_quantity: number;
        incoming_quantity: number;
        available_quantity: number;
    };
}

export interface ApiImageMatchAttempt {
    id: number;
    procurement_order_id: number;
    order_code?: string | null;
    procurement_line_id?: number | null;
    uploaded_image_url: string;
    model_name: string;
    model_version: string;
    query_embedding_checksum?: string | null;
    candidate_snapshot?: unknown;
    best_product_master_id?: number | null;
    best_distance?: number | null;
    decision: string;
    selected_product_master_id?: number | null;
    resolved_line_id?: number | null;
    reviewer_id?: number | null;
    resolved_at?: string | null;
    error_message?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface ApiProcurementImageMatchResponse {
    attempt: ApiImageMatchAttempt;
    candidates: ApiImageMatchCandidate[];
}

export interface ApiResolveImageMatchResponse {
    attempt: ApiImageMatchAttempt;
    line: ApiProcurementLine | null;
}

export interface ApiReceiveImageVerification {
    result: 'confirmed' | 'mismatch' | 'uncertain' | 'not_found' | string;
    decision: string;
    allow_receive: boolean;
    manual_review_required: boolean;
    reason: string;
    expected_product_master_id: number | null;
    expected_sku: string | null;
    expected_rank: number | null;
    expected_distance: number | null;
    expected_match_bucket: string | null;
    top_product_master_id: number | null;
    top_sku: string | null;
    top_distance: number | null;
    top_match_bucket: string | null;
}

export interface ApiReceiveImageMatchResponse {
    attempt: ApiImageMatchAttempt;
    verification: ApiReceiveImageVerification;
    candidates: ApiImageMatchCandidate[];
}

async function postProcurementPhoto<T>(path: string, photo: File, fields?: Record<string, string | number | undefined | null>): Promise<T> {
    const form = new FormData();
    form.append('photo', photo);
    Object.entries(fields ?? {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            form.append(key, String(value));
        }
    });

    const res = await erpFetch(`${BASE}${path}`, {
        method: 'POST',
        body: form,
    });
    if (!res.ok) throw await apiError(res);
    return res.json();
}

// Replace a line's 商品照片 with a newly captured image, then re-queue its poster.
export const replaceLineProductPhoto = (orderId: number, lineId: number, photo: File) =>
    postProcurementPhoto<ApiProcurementLine>(
        `/procurements/${orderId}/lines/${lineId}/replace-photo`,
        photo,
    );

export const createProcurementImageMatch = (orderId: number, payload: {
    photo: File;
    target_quantity?: number;
    supplier_id?: number;
    notes?: string;
}) => postProcurementPhoto<ApiProcurementImageMatchResponse>(
    `/procurements/${orderId}/image-match`,
    payload.photo,
    {
        target_quantity: payload.target_quantity,
        supplier_id: payload.supplier_id,
        notes: payload.notes,
    }
);

export const resolveProcurementImageMatch = (orderId: number, attemptId: number, payload: {
    action: 'restock_existing' | 'create_new_sku' | 'dismiss';
    product_master_id?: number;
    target_quantity?: number;
    supplier_id?: number;
    notes?: string;
    purchase_price?: number;
    purchase_currency?: string;
    height?: number;
    width?: number;
    depth?: number;
    internal_height?: number;
    internal_width?: number;
    internal_depth?: number;
    weight?: number;
    unit?: string;
    product_labels?: string[];
    attribute_option_ids?: number[];
}) => request<ApiResolveImageMatchResponse>(`/procurements/${orderId}/image-match/${attemptId}/resolve`, {
    method: 'POST',
    body: JSON.stringify(payload),
});

const productEditContext = (lineId?: number) => lineId == null ? '' : `?procurement_line_id=${lineId}`;

export const updateProductMaster = (masterId: number, payload: {
    name?: string | null;
    height?: number | null;
    width?: number | null;
    depth?: number | null;
    internal_height?: number | null;
    internal_width?: number | null;
    internal_depth?: number | null;
    weight?: number | null;
    unit?: string;
    category_id?: number | null;
    purchase_url?: string | null;
    opening_diameter?: number | null;
    attribute_ids?: number[];
    attribute_option_ids?: number[];
    product_labels?: string[];
    product_note?: string | null;
    supplier_id?: number;
    supplier_sku?: string | null;
}, procurementLineId?: number): Promise<ApiProductMasterUpdate> => {
    const url = `${ECOMMERCE_API_BASE_URL}/product/masters/${masterId}${productEditContext(procurementLineId)}`;
    return erpFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    }).then(async r => {
        if (!r.ok) throw await apiError(r);
        return r.json();
    });
};

export const replaceProductMasterPhoto = (masterId: number, photo: File, procurementLineId?: number): Promise<ApiProductMasterPhotoReplace> => {
    const form = new FormData();
    form.append('photo', photo);
    return erpFetch(`${PRODUCT_BASE}/masters/${masterId}/replace-photo${productEditContext(procurementLineId)}`, {
        method: 'POST',
        body: form,
    }).then(async r => {
        if (!r.ok) throw await apiError(r);
        return r.json();
    });
};

export async function addProductMasterImageAsset(
    masterId: number,
    options: { role: 'bottom' | 'other'; photo?: File; bottomNotApplicable?: boolean; sortOrder?: number },
    procurementLineId?: number,
): Promise<unknown> {
    const form = new FormData();
    form.append('role', options.role);
    if (options.photo) form.append('photo', options.photo);
    if (options.bottomNotApplicable !== undefined) {
        form.append('bottom_not_applicable', String(options.bottomNotApplicable));
    }
    if (options.sortOrder !== undefined) form.append('sort_order', String(options.sortOrder));
    const response = await erpFetch(`${PRODUCT_BASE}/masters/${masterId}/assets${productEditContext(procurementLineId)}`, {
        method: 'POST',
        body: form,
    });
    if (!response.ok) throw await apiError(response);
    return response.json();
}

export async function replaceProductMasterImageAsset(
    masterId: number,
    assetId: number,
    photo: File,
    procurementLineId?: number,
): Promise<unknown> {
    const form = new FormData();
    form.append('photo', photo);
    const response = await erpFetch(`${PRODUCT_BASE}/masters/${masterId}/assets/${assetId}/replace${productEditContext(procurementLineId)}`, {
        method: 'POST',
        body: form,
    });
    if (!response.ok) throw await apiError(response);
    return response.json();
}

export async function deleteProductMasterImageAsset(masterId: number, assetId: number, procurementLineId?: number): Promise<void> {
    const response = await erpFetch(`${PRODUCT_BASE}/masters/${masterId}/assets/${assetId}${productEditContext(procurementLineId)}`, { method: 'DELETE' });
    if (!response.ok) throw await apiError(response);
}

export const updateProcurementLine = (orderId: number, lineId: number, payload: {
    target_quantity?: number;
    notes?: string;
    supplier_id?: number | null;
    supplier_sku?: string | null;
    purchase_price?: number;
    purchase_currency?: string;
    min_purchase_quantity?: number | null;
    max_purchase_quantity?: number | null;
}) => request<ApiProcurementLine>(`/procurements/${orderId}/lines/${lineId}`, { method: 'PATCH', body: JSON.stringify(payload) });

export const confirmLinePurchase = (orderId: number, lineId: number, payload: {
    purchased_quantity: number;
    purchase_price: number;
    purchase_currency?: string;
}) => request<ApiProcurementLine>(`/procurements/${orderId}/lines/${lineId}/purchase`, { method: 'POST', body: JSON.stringify(payload) });

export const receiveLine = (orderId: number, lineId: number, payload: { receive_quantity: number; receipt_key?: string }) =>
    request<ApiProcurementLine>(`/procurements/${orderId}/lines/${lineId}/receive`, { method: 'POST', body: JSON.stringify(payload) });

export const verifyReceiveLineImageMatch = (orderId: number, lineId: number, photo: File) =>
    postProcurementPhoto<ApiReceiveImageMatchResponse>(`/procurements/${orderId}/lines/${lineId}/receive-image-match`, photo);

// 异常 is a flag + explanation note (broken, wrong quantity, etc.).
export const markLineException = (orderId: number, lineId: number, notes?: string) =>
    request<ApiProcurementLine>(`/procurements/${orderId}/lines/${lineId}/mark-exception`, {
        method: 'POST', body: JSON.stringify({ notes: notes ?? '' }),
    });

export const resolveLineException = (orderId: number, lineId: number) =>
    request<ApiProcurementLine>(`/procurements/${orderId}/lines/${lineId}/resolve-exception`, {
        method: 'POST', body: JSON.stringify({ action: 'resolve' }),
    });

// 待采购 → 缺货: 采购数=0 and cut this SKU's 直播预排 quantities.
export const markLineOutOfStock = (orderId: number, lineId: number) =>
    request<ApiProcurementLine>(`/procurements/${orderId}/lines/${lineId}/out-of-stock`, { method: 'POST' });

// 缺货 → 待采购 (does not restore already-cut 预排 quantities).
export const restoreLineToPending = (orderId: number, lineId: number) =>
    request<ApiProcurementLine>(`/procurements/${orderId}/lines/${lineId}/restore-pending`, { method: 'POST' });

export const deleteLineProcurement = (orderId: number, lineId: number) =>
    request<{ deleted: boolean; line_id: number }>(`/procurements/${orderId}/lines/${lineId}`, { method: 'DELETE' });

export const getV04MerchandisingPreview = (projectId: number, projectionVersion?: string, visualVersion?: string, profileVersion?: string, selectionSeed?: string) => {
    const query = new URLSearchParams();
    if (projectionVersion) query.set('projection_version', projectionVersion);
    if (visualVersion) query.set('visual_version', visualVersion);
    if (profileVersion) query.set('profile_version', profileVersion);
    if (selectionSeed) query.set('selection_seed', selectionSeed);
    return request<ApiMerchandisingPreview>(`/ai-planning/projects/${projectId}/v0.4-merchandising-preview?${query}`);
};
export const generateV04MerchandisingPlan = (projectId: number, projectionVersion?: string, visualVersion?: string, profileVersion?: string) =>
    request<ApiSavedMerchandisingPlan>(`/ai-planning/projects/${projectId}/v0.4-merchandising-plans`, { method: 'POST', body: JSON.stringify({ projection_version: projectionVersion, visual_version: visualVersion, profile_version: profileVersion }) });

export const generateLivePlanDraft = (projectId: number, payload: {
    planning_brief?: string;
    selection_mode?: 'free_text' | 'template';
    procurement_order_ids?: number[];
    sales_theme_enabled?: boolean;
    sales_theme_count?: number;
    profile_version?: string;
    projection_version?: string;
    visual_version?: string;
    selection_seed?: string;
    warehouse_id: number;
}) => request<ApiLivePlanDraft>(`/ai-planning/projects/${projectId}/live-plan-drafts`, {
    method: 'POST', body: JSON.stringify(payload),
});

export const applyLivestreamAiPlan = (liveId: number, payload: { application_token: string; mode: 'groups' | 'groups_and_skus' }) =>
    request<ApiLivestreamPlanImportCommitResult & { plan_id: string; idempotent: boolean }>(`/livestreams/${liveId}/ai-plan-applications`, {
        method: 'POST', body: JSON.stringify(payload),
    });

export interface ApiProcurementProductDetail {
    detail: ApiInventoryDetail;
    read_only: boolean;
    snapshot_missing: boolean;
    purchase: { supplier_name?: string | null; accepted_at: string | null; purchased_quantity: number; purchase_price: number | null; purchase_currency: string | null };
}
export const getProcurementProductDetail = (orderId: number, lineId: number, signal?: AbortSignal) =>
    request<ApiProcurementProductDetail>(`/procurements/${orderId}/lines/${lineId}/product-detail`, { signal });
export const correctAvailableInventory = (productId: number, warehouses: Array<{
    warehouse_id: number; available_quantity: number; expected_available_quantity: number;
}>) => request<ApiInventoryDetail>(`/inventory/${productId}/available`, { method: 'PATCH', body: JSON.stringify({ warehouses }) });
